import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Banknote, Clock, CreditCard, Eye, EyeOff, History, Minus, Phone, Plus,
  ScanBarcode, Search, ShoppingCart, Ticket, Trash2, Undo2, Redo2, Wallet, X,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import { useProducts } from '../../context/ProductsContext';
import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import useSessionDraft from '../../hooks/useSessionDraft';
import { formatCurrency } from '../../data/mockData';
import {
  getProduct, listProducts, lookupByBarcode, listLots,
} from '../../api/inventory';
import { COUPON_VALIDATION_ERRORS, validateCoupon } from '../../api/promotions';
import {
  amendPosSale, createPosSale, getPosSale, instantOnboardPos, listPosSales, mapPosAmendError,
  mapPosChargeError, mapPosInstantOnboardError, normalizePosSalePayload,
  POS_CHARGE_ERRORS, posSalesDateIst, previewPosAmend,
} from '../../api/pos';
import { newClientPaymentId } from '../../api/payLaters';
import { allocatePosCustomerNumber, ensurePosCustomerCountAtLeast } from '../../utils/posCustomerNumber';
import { loadPosCounter, savePosCounter } from '../../utils/posCounter';
import { clearPosSaleCache, loadPosSaleCache, savePosSaleCache } from '../../utils/posSaleCache';
import { savePosTenderLines } from '../../utils/posTenderCache';
import { POS_COUNTER, POS_GSTIN } from '../../utils/escpos';
import { canAmendPos } from '../../auth/routing';
import { cartLineKey, allocateOfflineQty, defaultOpenLot, posDisplayPrice, resolveLotForCart, resolveOpenLotsForLine } from '../../utils/lots';
import { posCatalogCache } from '../../utils/posCatalogCache';
import {
  calculateBillSavings,
  formatDiscountLabel,
  formatDiscountPercent,
  formatItemSavingsText,
  belowMinOrderMessage,
  getItemSavings,
  lineBelowMinOrder,
  minimumPackQty,
  payableForTender,
  payLaterHeadline,
  phoneDigits,
} from '../../utils/posMoney';
import {
  SOLD_BY, cartQtyFooter, cartQtyIssue, formatPrice, formatQty, formatStock, formatWeightKg, isSoldByWeight, lineAmount, orderQtyProblem, quantizeKg, roundMoney,
} from '../../utils/soldBy';
import Receipt from './Receipt';
import LotPickerModal from './LotPickerModal';
import WeighSheetModal from './WeighSheetModal';
import PosHistoryModal from './PosHistoryModal';
import PosAmendSettlementModal from './PosAmendSettlementModal';
import InstantOnboardModal from './InstantOnboardModal';
import PayLaterCustomerModal from './PayLaterCustomerModal';
import {
  buildProvisionalReceipt,
  canDiscardCharge,
  canEditCharge,
  cartStockIssue,
  isCreditCharge,
  localReference,
  posOfflineQueue,
  queuedBillAmount,
  reservedQty,
  subscribeOfflineQueue,
  useBrowserOnline,
} from '../../utils/posOfflineQueue';
import styles from './POS.module.css';

const PAYMENT_METHODS = [
  { key: 'CASH', label: 'Cash', icon: Banknote },
  { key: 'UPI', label: 'UPI', icon: Wallet },
  { key: 'CARD', label: 'Card', icon: CreditCard },
  { key: 'PAY_LATER', label: 'Pay Later', icon: Clock },
];

/** Amend / settle APIs only accept cash tenders — hide Pay Later in edit mode. */
const EDIT_PAYMENT_METHODS = PAYMENT_METHODS.filter((m) => m.key !== 'PAY_LATER');

const COUNTED_SALE_KEY = 'bhima-pos.counted-sale-ids';
const CATALOG_IDLE_SYNC_MS = 60000;
const STOCK_REFRESH_CODES = new Set(['insufficient_stock', 'item_unavailable', 'lot_mismatch']);

function readCountedSaleIds() {
  try {
    const raw = sessionStorage.getItem(COUNTED_SALE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function rememberCountedSale(clientSaleId) {
  if (!clientSaleId) return false;
  const seen = readCountedSaleIds();
  if (seen.has(clientSaleId)) return false;
  seen.add(clientSaleId);
  try {
    sessionStorage.setItem(COUNTED_SALE_KEY, JSON.stringify([...seen].slice(-200)));
  } catch {
    // Session totals still update for this page view.
  }
  return true;
}

function buildCartFromSale(sale, products) {
  const lineItems = Array.isArray(sale?.line_items) ? sale.line_items : [];
  const credits = {};
  const cart = [];

  lineItems.forEach((li, index) => {
    const itemId = li.item_id || li.id;
    if (!itemId) return;
    const lotId = li.lot_id || li.lotId || null;
    const product = products.find((p) => p.id === itemId) || {};
    const lineSoldBy = String(li.sold_by || li.soldBy || product.soldBy || '').toUpperCase() === 'WEIGHT'
      ? SOLD_BY.WEIGHT
      : SOLD_BY.EACH;
    const qtyRaw = Number(li.qty);
    if (!Number.isFinite(qtyRaw) || qtyRaw <= 0) return;
    const qty = lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(qtyRaw) : qtyRaw;
    const lineKey = cartLineKey(itemId, lotId);
    credits[lineKey] = (credits[lineKey] || 0) + qty;
    const catalogStock = Number(product.stock) || 0;
    cart.push({
      lineKey,
      id: itemId,
      lotId,
      requiresLot: Boolean(lotId),
      name: li.name || product.name || itemId,
      sku: li.sku || product.sku || '',
      price: Number(li.unit_price ?? li.price ?? product.price) || 0,
      mrp: li.mrp ?? product.mrp,
      unit: li.unit || product.unit || (lineSoldBy === SOLD_BY.WEIGHT ? 'kg' : 'pcs'),
      soldBy: lineSoldBy,
      qtyStep: product.qtyStep ?? (lineSoldBy === SOLD_BY.WEIGHT ? 0.001 : 1),
      minOrderQty: product.minOrderQty ?? (lineSoldBy === SOLD_BY.WEIGHT ? 0.001 : 1),
      maxOrderQty: product.maxOrderQty ?? 10,
      emoji: product.emoji || '🛒',
      swatch: product.swatch || '#c4b59a',
      stock: catalogStock,
      gstRate: product.gstRate || li.gst_rate || 0,
      qty,
      _hydrateIndex: index,
    });
  });

  return { cart, credits };
}

/** Prefer receipt for print; merge sale fields for revision / ids. */
function buildConfirmedAmendReceipt(sale, receipt, fallback = {}) {
  const print = receipt && typeof receipt === 'object' ? receipt : {};
  const meta = sale && typeof sale === 'object' ? sale : {};
  const revision = print.receipt_revision ?? meta.receipt_revision ?? fallback.receipt_revision;
  return {
    ...meta,
    ...print,
    order_id: print.order_id || meta.order_id || fallback.order_id || null,
    display_id: print.display_id || meta.display_id || fallback.display_id || null,
    line_items: print.line_items || meta.line_items || [],
    total: print.total ?? meta.total,
    subtotal: print.subtotal ?? meta.subtotal,
    discount_amount: print.discount_amount ?? meta.discount_amount,
    discount_percent: print.discount_percent ?? meta.discount_percent ?? fallback.discount_percent,
    payment_method: print.payment_method || meta.payment_method,
    coupon_code: print.coupon_code ?? meta.coupon_code ?? null,
    gst_breakup: print.gst_breakup || meta.gst_breakup || [],
    round_off: print.round_off ?? meta.round_off ?? 0,
    customer_phone: print.customer_phone ?? meta.customer_phone ?? null,
    created_at: print.created_at || meta.created_at || meta.updated_at || new Date().toISOString(),
    receipt_revision: revision != null ? Number(revision) : 2,
    revised: print.revised === true || meta.revised === true || Number(revision) > 1,
    note: print.note || meta.note || '(Inclusive of GST)',
  };
}

function salePayloadFromGetResponse(data, orderId) {
  return normalizePosSalePayload(data, { order_id: orderId });
}

function minOrderChargeError(lines) {
  const hit = lineBelowMinOrder(lines);
  if (!hit) return null;
  const err = new Error(belowMinOrderMessage({
    name: hit.line.name,
    min: hit.floor,
    itemId: hit.line.id,
  }));
  err.code = 'below_min_order_qty';
  err.body = {
    item_id: hit.line.id,
    item_name: hit.line.name,
    min_order_qty: hit.floor,
  };
  return err;
}

export default function POS() {
  const { products, reloadProducts, mergeProducts, syncCatalog } = useProducts();
  const { user } = useAuth();
  const { notify } = useOrders();
  const canEditPosBills = canAmendPos(user?.groups);

  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [captureFocused, setCaptureFocused] = useState(true);
  const storedCounter = loadPosCounter(user?.sub);
  const [counterNumber, setCounterNumber] = useState(storedCounter);
  const [counterSetupOpen, setCounterSetupOpen] = useState(!storedCounter);
  const [counterSetupError, setCounterSetupError] = useState('');
  const [instantOnboard, setInstantOnboard] = useState(null);
  const [cart, setCart] = useState([]);
  const browserOnline = useBrowserOnline();
  const [restoredLotsPending, setRestoredLotsPending] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [cartSearch, setCartSearch] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const restoredLinesRef = useRef([]);
  const posSaleHydratedRef = useRef(false);
  const skipEmptySaleClearRef = useRef(false);
  const lotSyncBusyRef = useRef(false);
  const lotSyncStateRef = useRef({});
  const chargeAttemptRef = useRef(null);

  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const [selectedMethods, setSelectedMethods] = useState([]);
  const [paymentAmounts, setPaymentAmounts] = useState({});

  const [receipt, setReceipt] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [reprintOpen, setReprintOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editSale, setEditSale] = useState(null); // { orderId, displayId, couponLocked, couponCode }
  const [amendStockCredit, setAmendStockCredit] = useState({});
  const [amendPreview, setAmendPreview] = useState(null);
  const [amending, setAmending] = useState(false);
  const [repreviewing, setRepreviewing] = useState(false);
  const [amendError, setAmendError] = useState('');
  const [changeDue, setChangeDue] = useState(0);
  const [chargeError, setChargeError] = useState('');
  const [charging, setCharging] = useState(false);
  const [payLaterOpen, setPayLaterOpen] = useState(false);
  const [payLaterLotNote, setPayLaterLotNote] = useState(false);
  const [offlineSnap, setOfflineSnap] = useState({ charges: [], settles: [] });
  const [sessionStats, setSessionStats] = useState({ count: 0, total: 0 });
  const [salesHidden, setSalesHidden] = useState(() => {
    try {
      return localStorage.getItem('bhima-store.pos-sales-hidden') === '1';
    } catch {
      return false;
    }
  });
  const [lotPick, setLotPick] = useState(null); // { item, lots }
  const [weighPick, setWeighPick] = useState(null); // weigh sheet context

  const scanRef = useRef(null);
  const scanQueueRef = useRef([]);
  const processingScanRef = useRef(false);
  const scanRefocusTimerRef = useRef(null);
  const scanOverlayOpenRef = useRef(false);
  const isEditMode = Boolean(editSale?.orderId);

  // Keep the chosen counter across refreshes for this signed-in user.
  useEffect(() => {
    const normalized = counterNumber.trim().toUpperCase();
    if (user?.sub && /^[A-Z0-9]{1,16}$/.test(normalized)) {
      savePosCounter(user.sub, normalized);
    }
  }, [counterNumber, user?.sub]);

  // "Sales today" lives on the server — reload after refresh so the header stays accurate.
  const refreshSessionStats = useCallback(async () => {
    if (!user?.idToken) return;
    try {
      const data = await listPosSales({
        date: posSalesDateIst(),
        scope: 'mine',
        limit: 1,
      }, user.idToken);
      const summary = data?.summary;
      if (!summary || typeof summary !== 'object') return;
      const billCount = Number(summary.bill_count) || 0;
      ensurePosCustomerCountAtLeast(billCount);
      setSessionStats({
        count: billCount,
        total: Number(summary.gross_total) || 0,
      });
    } catch {
      // Leave current totals; charging will still update the header for this visit.
    }
  }, [user?.idToken]);

  useEffect(() => {
    refreshSessionStats();
  }, [refreshSessionStats]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      posOfflineQueue.snapshot()
        .then((snap) => { if (!cancelled) setOfflineSnap(snap); })
        .catch(() => {});
      if (typeof navigator === 'undefined' || navigator.onLine !== false) refreshSessionStats();
    };
    load();
    return subscribeOfflineQueue(load);
  }, [refreshSessionStats]);

  useEffect(() => {
    if (!user?.idToken) return undefined;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return undefined;
    let cancelled = false;
    offlineSnap.charges.forEach((row) => {
      if (row.orderId || row.conflict || !canEditCharge(row)) return;
      const items = row.items || [];
      if (!items.some((line) => !line.lot_id)) return;
      (async () => {
        const next = [];
        let changed = false;
        for (const line of items) {
          if (cancelled) return;
          if (line.lot_id) {
            next.push(line);
            continue;
          }
          let lots = products.find((item) => item.id === line.item_id)?.lots;
          if (!Array.isArray(lots) || !lots.length) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const listed = await listLots(line.item_id, user.idToken);
              lots = listed.lots || [];
            } catch {
              lots = [];
            }
          }
          const lot = defaultOpenLot(lots, line.qty, {
            allowShortLots: line.soldBy === SOLD_BY.WEIGHT,
          });
          if (!lot?.lotId) {
            next.push(line);
            continue;
          }
          changed = true;
          const price = Number(lot.sellingPrice);
          next.push({
            ...line,
            lot_id: lot.lotId,
            requiresLot: false,
            price: Number.isFinite(price) ? price : line.price,
          });
        }
        if (cancelled || !changed) return;
        posOfflineQueue.updateCharge(row.clientSaleId, {
          items: next,
          needsLot: next.some((line) => line.requiresLot && !line.lot_id),
          errorCode: null,
          errorMessage: null,
        });
      })();
    });
    return () => { cancelled = true; };
  }, [offlineSnap, products, user?.idToken]);

  useEffect(() => {
    if (isEditMode || cart.length > 0) return undefined;
    const id = setInterval(() => { syncCatalog(); }, CATALOG_IDLE_SYNC_MS);
    const onOnline = () => { syncCatalog(); };
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(id);
      window.removeEventListener('online', onOnline);
    };
  }, [isEditMode, cart.length, syncCatalog]);

  const applyCachedSale = (saved) => {
    if (!saved) return;
    const hasCart = Array.isArray(saved.cart) && saved.cart.length > 0;
    if (!hasCart && !saved.editSale?.orderId) return;
    if (Array.isArray(saved.cart)) setCart(saved.cart);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoStack([]);
    setRedoStack([]);
    setQtyDrafts(saved.qtyDrafts && typeof saved.qtyDrafts === 'object' ? saved.qtyDrafts : {});
    if (saved.couponInput) setCouponInput(saved.couponInput);
    if (saved.appliedCoupon) setAppliedCoupon(saved.appliedCoupon);
    if (Array.isArray(saved.selectedMethods)) setSelectedMethods(saved.selectedMethods);
    if (saved.paymentAmounts && typeof saved.paymentAmounts === 'object') {
      setPaymentAmounts(saved.paymentAmounts);
    }
    if (saved.customerPhone) setCustomerPhone(String(saved.customerPhone).replace(/\D/g, '').slice(0, 10));
    if (saved.editSale?.orderId) {
      setEditSale(saved.editSale);
      setAmendStockCredit(saved.amendStockCredit && typeof saved.amendStockCredit === 'object'
        ? saved.amendStockCredit
        : {});
    }
    if (Array.isArray(saved.cart) && saved.cart.length) {
      restoredLinesRef.current = saved.cart;
      const online = typeof navigator === 'undefined' || navigator.onLine;
      if (online) setRestoredLotsPending(true);
    }
  };

  // Keep the open sale when the cashier leaves POS for Inventory, Products, or
  // Bulk onboarding. The screen unmounts on those routes; this puts the cart back.
  useEffect(() => {
    if (!user?.sub || posSaleHydratedRef.current) return;
    posSaleHydratedRef.current = true;
    const saved = loadPosSaleCache(user.sub);
    const canRestore = Boolean(
      (Array.isArray(saved?.cart) && saved.cart.length) || saved?.editSale?.orderId,
    );
    if (!canRestore) return;
    skipEmptySaleClearRef.current = true;
    applyCachedSale(saved);
    // Restore once per visit. applyCachedSale closes over stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  // An unfinished sale survives an inactivity sign-out and comes back when the
  // same person signs in again — nothing is re-keyed at the counter.
  // Skip while amending an existing bill so we don't restore a half-edit later.
  useSessionDraft(
    'pos.sale',
    !isEditMode && cart.length
      ? {
        cart, couponInput, appliedCoupon, selectedMethods, paymentAmounts, counterNumber, customerPhone,
      }
      : undefined,
    (saved) => {
      if (isEditMode) return;
      if (!Array.isArray(saved?.cart) || !saved.cart.length) return;
      setCart(saved.cart);
      undoStackRef.current = [];
      redoStackRef.current = [];
      setUndoStack([]);
      setRedoStack([]);
      if (saved.couponInput) setCouponInput(saved.couponInput);
      if (saved.appliedCoupon) setAppliedCoupon(saved.appliedCoupon);
      if (Array.isArray(saved.selectedMethods)) setSelectedMethods(saved.selectedMethods);
      if (saved.paymentAmounts) setPaymentAmounts(saved.paymentAmounts);
      if (saved.customerPhone) setCustomerPhone(String(saved.customerPhone).replace(/\D/g, '').slice(0, 10));
      if (saved.counterNumber) {
        setCounterNumber(saved.counterNumber);
        setCounterSetupOpen(false);
      }
      const online = typeof navigator === 'undefined' || navigator.onLine;
      if (online) {
        restoredLinesRef.current = saved.cart;
        setRestoredLotsPending(true);
      }
      notify('Restored the sale you had in progress.');
    },
  );

  useEffect(() => {
    if (!user?.sub || !posSaleHydratedRef.current) return;
    const hasCart = cart.length > 0;
    const hasEdit = Boolean(editSale?.orderId);
    if (!hasCart && !hasEdit) {
      if (skipEmptySaleClearRef.current) {
        skipEmptySaleClearRef.current = false;
        return;
      }
      clearPosSaleCache();
      return;
    }
    skipEmptySaleClearRef.current = false;
    savePosSaleCache(user.sub, {
      cart,
      qtyDrafts,
      couponInput,
      appliedCoupon,
      selectedMethods,
      paymentAmounts,
      customerPhone,
      editSale,
      amendStockCredit,
    });
  }, [
    user?.sub,
    cart,
    qtyDrafts,
    couponInput,
    appliedCoupon,
    selectedMethods,
    paymentAmounts,
    customerPhone,
    editSale,
    amendStockCredit,
  ]);

  const scanOverlayOpen = Boolean(
    receipt || reprintOpen || historyOpen || amendPreview || lotPick || weighPick
      || instantOnboard || counterSetupOpen || payLaterOpen,
  );
  scanOverlayOpenRef.current = scanOverlayOpen;

  const isEditableTarget = (el) => {
    if (!el || el === scanRef.current) return false;
    const tag = String(el.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return false;
  };

  const focusScanField = useCallback(() => {
    if (scanOverlayOpenRef.current) return;
    scanRef.current?.focus({ preventScroll: true });
  }, []);

  const scheduleScanRefocus = useCallback((relatedTarget) => {
    if (scanOverlayOpenRef.current) return;
    if (isEditableTarget(relatedTarget)) return;
    if (scanRefocusTimerRef.current) clearTimeout(scanRefocusTimerRef.current);
    scanRefocusTimerRef.current = setTimeout(() => {
      scanRefocusTimerRef.current = null;
      const active = document.activeElement;
      if (isEditableTarget(active)) return;
      if (scanOverlayOpenRef.current) return;
      scanRef.current?.focus({ preventScroll: true });
    }, 0);
  }, []);

  // Keep barcode capture focused whenever overlays close / on mount.
  useEffect(() => {
    if (scanOverlayOpen) return undefined;
    focusScanField();
    return undefined;
  }, [scanOverlayOpen, focusScanField]);

  // Return focus when the cashier comes back to the POS tab/window.
  useEffect(() => {
    const onWindowFocus = () => focusScanField();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') focusScanField();
    };
    window.addEventListener('focus', onWindowFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onWindowFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (scanRefocusTimerRef.current) clearTimeout(scanRefocusTimerRef.current);
    };
  }, [focusScanField]);

  const activeProducts = useMemo(() => products.filter((p) => p.status === 'ACTIVE'), [products]);

  /** O(1) exact barcode hits for ACTIVE / inactive catalog rows. */
  const activeByBarcode = useMemo(() => {
    const map = new Map();
    activeProducts.forEach((product) => {
      const code = String(product.barcode || '').trim();
      if (code && !map.has(code)) map.set(code, product);
    });
    return map;
  }, [activeProducts]);

  const inactiveByBarcode = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      if (product.status === 'ACTIVE') return;
      const code = String(product.barcode || '').trim();
      if (code && !map.has(code)) map.set(code, product);
    });
    return map;
  }, [products]);

  const query = scanValue.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!query) return [];
    return activeProducts.filter((p) => (
      p.name.toLowerCase().includes(query)
      || p.sku.toLowerCase().includes(query)
      || (p.barcode || '').includes(query)
    ));
  }, [query, activeProducts]);

  const cartQtyByLineKey = useMemo(
    () => Object.fromEntries(cart.map((l) => [l.lineKey, l.qty])),
    [cart],
  );

  const cartQtyByItemId = useMemo(() => {
    const map = {};
    cart.forEach((l) => {
      map[l.id] = (map[l.id] || 0) + l.qty;
    });
    return map;
  }, [cart]);

  const amendCreditByItemId = useMemo(() => {
    if (!isEditMode) return {};
    const map = {};
    Object.entries(amendStockCredit).forEach(([lineKey, qty]) => {
      const itemId = String(lineKey).split('::')[0];
      map[itemId] = (map[itemId] || 0) + (Number(qty) || 0);
    });
    return map;
  }, [isEditMode, amendStockCredit]);

  const tileCartQty = (itemId) => {
    const inCart = cartQtyByItemId[itemId] || 0;
    if (!isEditMode) return inCart;
    return Math.max(0, inCart - (amendCreditByItemId[itemId] || 0));
  };

  const resolveProduct = (product) => {
    const local = products.find((p) => p.id === product.id);
    return local || product;
  };

  const commitUndoStack = (next) => {
    undoStackRef.current = next;
    setUndoStack(next);
  };

  const commitRedoStack = (next) => {
    redoStackRef.current = next;
    setRedoStack(next);
  };

  const clearEditHistory = () => {
    commitUndoStack([]);
    commitRedoStack([]);
  };

  const pushQtyChange = (entry) => {
    if (entry.fromQty === entry.toQty) return;
    commitUndoStack([...undoStackRef.current, entry]);
    commitRedoStack([]);
  };

  const applyQtyEntry = (entry, direction) => {
    const qty = direction === 'undo' ? entry.fromQty : entry.toQty;
    setQtyDrafts((drafts) => {
      if (drafts[entry.line.lineKey] === undefined) return drafts;
      const next = { ...drafts };
      delete next[entry.line.lineKey];
      return next;
    });
    setCart((prev) => {
      const key = entry.line.lineKey;
      if (qty == null) return prev.filter((l) => l.lineKey !== key);
      if (!prev.some((l) => l.lineKey === key)) return [{ ...entry.line, qty }, ...prev];
      return prev.map((l) => (l.lineKey === key ? { ...l, qty } : l));
    });
  };

  const holdPaymentForMinOrder = (lines) => {
    const err = minOrderChargeError(lines);
    if (!err) {
      setChargeError((prev) => (/need at least/i.test(String(prev)) ? '' : prev));
      return false;
    }
    setChargeError(err.message);
    setSelectedMethods([]);
    setPaymentAmounts({});
    setPayLaterOpen(false);
    return true;
  };

  const addToCart = (product, {
    keepValue,
    lotId = null,
    unitPrice,
    lotQty,
    mrp,
    requiresLot = false,
    qty = 1,
    soldBy,
    replaceQty = false,
    fromCache = false,
    lotChoice = null,
  } = {}) => {
    const item = resolveProduct(product);
    if (requiresLot && !lotId) {
      setScanError(`Select a lot for ${item.name} before adding to cart`);
      return false;
    }
    const lineSoldBy = soldBy || (isSoldByWeight(item) ? SOLD_BY.WEIGHT : SOLD_BY.EACH);
    const lineKey = cartLineKey(item.id, lotId);
    const price = unitPrice !== undefined && unitPrice !== null ? Number(unitPrice) : Number(item.price) || 0;
    const stockCap = lotQty !== undefined && lotQty !== null ? Number(lotQty) : Number(item.stock) || 0;
    const canGapFill = item.catalogComplete === false;
    const existingLine = cart.find((l) => l.lineKey === lineKey);
    const addQty = Number(qty);
    if (!Number.isFinite(addQty) || addQty <= 0) {
      setScanError('Enter a valid quantity.');
      return false;
    }
    const nextQtyRaw = replaceQty ? addQty : (cartQtyByLineKey[lineKey] || 0) + addQty;
    const nextQty = lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(nextQtyRaw) : nextQtyRaw;
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    const reserved = isEditMode ? 0 : reservedQty(offlineSnap.charges, item.id, lotId);
    const sellable = stockCap - reserved + stockCredit;
    const allowZeroCache = fromCache && !existingLine && stockCap <= 0;
    if (!canGapFill && !allowZeroCache && nextQty > sellable) {
      const left = Math.max(0, sellable);
      setScanError(
        left <= 0
          ? `${item.name} is out of stock${lotId ? ' on this lot' : ''}`
          : lineSoldBy === SOLD_BY.WEIGHT
            ? `Only ${formatWeightKg(left)} of ${item.name} left`
            : `Only ${left} of ${item.name} left`,
      );
      return false;
    }
    const toQty = existingLine
      ? nextQty
      : (lineSoldBy === SOLD_BY.WEIGHT ? quantizeKg(addQty) : addQty);
    const fromQty = existingLine ? existingLine.qty : null;
    const historyLine = existingLine
      ? {
        ...existingLine,
        qty: toQty,
        price,
        stock: stockCap,
        soldBy: lineSoldBy,
        qtyStep: item.qtyStep,
        minOrderQty: item.minOrderQty,
        maxOrderQty: item.maxOrderQty,
        catalogComplete: item.catalogComplete,
        onboardSource: item.onboardSource,
      }
      : {
        lineKey,
        id: item.id,
        lotId: lotId || null,
        requiresLot: Boolean(requiresLot || lotId),
        lotChoice: lotId ? (lotChoice || 'auto') : undefined,
        name: item.name,
        sku: item.sku,
        price,
        mrp: mrp ?? item.mrp,
        unit: item.unit,
        soldBy: lineSoldBy,
        qtyStep: item.qtyStep,
        minOrderQty: item.minOrderQty,
        maxOrderQty: item.maxOrderQty,
        emoji: item.emoji,
        swatch: item.swatch,
        stock: stockCap,
        gstRate: item.gstRate || 0,
        catalogComplete: item.catalogComplete,
        onboardSource: item.onboardSource,
        priceUnconfirmed: Boolean(fromCache || existingLine?.priceUnconfirmed),
        qty: toQty,
      };
    setCart((prev) => {
      const existing = prev.find((l) => l.lineKey === lineKey);
      if (existing) {
        return prev.map((l) => (l.lineKey === lineKey ? { ...historyLine, qty: toQty } : l));
      }
      return [{ ...historyLine, qty: toQty }, ...prev];
    });
    pushQtyChange({ line: { ...historyLine, qty: toQty }, fromQty, toQty });
    const qtyProblem = orderQtyProblem(toQty, item);
    setScanError(qtyProblem ? `${item.name}: ${qtyProblem}` : '');
    const nextLines = existingLine
      ? cart.map((l) => (l.lineKey === lineKey ? { ...l, qty: toQty } : l))
      : [{ ...historyLine, qty: toQty }, ...cart];
    holdPaymentForMinOrder(nextLines);
    setFlashId(lineKey);
    setTimeout(() => setFlashId((id) => (id === lineKey ? null : id)), 900);
    if (!keepValue) setScanValue('');
    focusScanField();
    return true;
  };

  const openWeighSheet = ({
    item, unitPrice, lotQty, mrp, requiresLot = false, lotId = null, lotChoice = null,
    keepValue = false, mode = 'add', initialWeight = '', fromCache = false,
    spillLots = false, allocationLots = null, allocationTaken = null, editLotCap = null,
  }) => {
    setWeighPick({
      item,
      fromCache,
      unitPrice: unitPrice !== undefined && unitPrice !== null ? Number(unitPrice) : Number(item.price) || 0,
      stockCap: lotQty !== undefined && lotQty !== null ? Number(lotQty) : Number(item.stock) || 0,
      mrp,
      requiresLot,
      lotId,
      lotChoice: lotId ? (lotChoice || 'auto') : undefined,
      keepValue,
      mode,
      initialWeight,
      qtyStep: item.qtyStep,
      minOrderQty: item.minOrderQty,
      maxOrderQty: item.maxOrderQty,
      lineKey: mode === 'edit' ? cartLineKey(item.id, lotId) : null,
      spillLots,
      allocationLots,
      allocationTaken,
      editLotCap: editLotCap != null ? Number(editLotCap) : null,
    });
  };

  const applyResolvedLot = (resolved, { keepValue, qty = 1 } = {}) => {
    if (resolved.action === 'pick_lot') {
      setLotPick({
        item: resolved.item,
        lots: resolved.lots,
        ...(qty !== 1 ? { instantEntry: true, qty } : {}),
      });
      if (!keepValue) setScanValue('');
      return;
    }
    // lot_pick_required flipped off (one shelf price). Drop a picker for this item.
    setLotPick((prev) => (prev?.item?.id === resolved.item?.id ? null : prev));
    if (resolved.requiresLot && !resolved.lotId) {
      setScanError(`${resolved.item.name} has open lots — pick a pack price`);
      return;
    }
    if (isSoldByWeight(resolved.item)) {
      openWeighSheet({
        item: resolved.item,
        unitPrice: resolved.unitPrice,
        lotQty: resolved.lotQty,
        mrp: resolved.mrp,
        requiresLot: resolved.requiresLot,
        lotId: resolved.lotId,
        keepValue,
        mode: 'add',
      });
      return;
    }
    addToCart(resolved.item, {
      keepValue,
      lotId: resolved.lotId,
      unitPrice: resolved.unitPrice,
      lotQty: resolved.lotQty,
      mrp: resolved.mrp,
      requiresLot: resolved.requiresLot,
    });
  };

  const rememberProductLots = (itemId, lots) => {
    if (!itemId || !Array.isArray(lots)) return;
    mergeProducts([{ id: itemId, lots }]);
  };

  const resolveProductWithLots = async (product) => {
    const item = resolveProduct(product);
    try {
      const { lots } = await listLots(item.id, user.idToken);
      rememberProductLots(item.id, lots);
      return resolveLotForCart({
        item,
        lots,
        hasLots: true,
      });
    } catch (err) {
      if (err.status === 404 || err.message === 'not_found') {
        return resolveLotForCart({ item, lots: null, lotPickRequired: false, hasLots: false });
      }
      throw err;
    }
  };

  const openInstantOnboard = (mode, barcode = '') => {
    if (!browserOnline) {
      setScanError('Not in catalog. Add it in Products.');
      return;
    }
    setInstantOnboard({ mode, barcode });
    setScanError('');
  };

  useEffect(() => {
    if (browserOnline || !instantOnboard) return;
    setInstantOnboard(null);
    setScanError('Not in catalog. Add it in Products.');
  }, [browserOnline, instantOnboard]);

  const addExistingInstantProduct = async (product, qty, lookupData) => {
    const resolved = lookupData
      ? resolveLotForCart(lookupData)
      : await resolveProductWithLots(product);
    if (resolved.action === 'pick_lot') {
      setInstantOnboard(null);
      setLotPick({
        item: resolved.item,
        lots: resolved.lots,
        instantEntry: true,
        qty,
      });
      return;
    }
    const added = addToCart(resolved.item, {
      lotId: resolved.lotId,
      unitPrice: resolved.unitPrice,
      lotQty: resolved.lotQty,
      mrp: resolved.mrp,
      requiresLot: resolved.requiresLot,
      qty,
      soldBy: resolved.item.soldBy,
    });
    if (!added) throw new Error('This product cannot be added with the entered quantity.');
    setInstantOnboard(null);
  };

  const handleInstantOnboard = async (payload) => {
    if (!browserOnline) {
      setInstantOnboard(null);
      setScanError('Not in catalog. Add it in Products.');
      return;
    }
    try {
      const result = await instantOnboardPos(payload, user.idToken);
      mergeProducts([result.item]);
      const lot = result.lots.find((entry) => entry.lotId === result.lotId) || result.lots[0];
      if (lot) {
        posCatalogCache.upsert([{
          ...result.item,
          price: lot.sellingPrice ?? result.item.price,
          mrp: lot.mrp ?? result.item.mrp,
          stock: Math.max(Number(lot.qty) || 0, Number(payload.qty) || 0),
        }], { source: 'lot' }).catch(() => {});
      }
      const added = addToCart(result.item, {
        lotId: result.lotId,
        unitPrice: lot?.sellingPrice ?? result.item.price,
        lotQty: Math.max(Number(lot?.qty) || 0, Number(payload.qty) || 0),
        mrp: lot?.mrp ?? result.item.mrp,
        requiresLot: Boolean(result.lotId),
        qty: payload.qty,
        soldBy: payload.sold_by,
      });
      if (!added) throw new Error('The new item could not be added to the cart.');
      setInstantOnboard(null);
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'barcode_taken' && payload.barcode) {
        const existing = await lookupByBarcode(payload.barcode, user.idToken);
        mergeProducts([existing.item]);
        await addExistingInstantProduct(existing.item, Number(payload.qty), existing);
        return;
      }
      if (code === 'name_taken' && err?.body?.item_id) {
        const existing = await getProduct(err.body.item_id, user.idToken);
        mergeProducts([existing]);
        await addExistingInstantProduct(existing, Number(payload.qty));
        return;
      }
      if (code === 'name_taken') {
        const { items } = await listProducts(user.idToken, {
          q: payload.name,
          limit: 50,
          includeInactive: true,
        });
        const normalizedName = String(payload.name || '').trim().toLowerCase();
        const existing = items.find((item) => (
          item.name.trim().toLowerCase() === normalizedName
          && item.status === 'ACTIVE'
          && (payload.sold_by === 'WEIGHT' ? item.soldBy === 'WEIGHT' : item.soldBy !== 'WEIGHT')
        ));
        if (existing) {
          mergeProducts([existing]);
          await addExistingInstantProduct(existing, Number(payload.qty));
          return;
        }
      }
      throw new Error(mapPosInstantOnboardError(err));
    }
  };

  const persistResolvedLookup = (data) => {
    if (data?.item?.id && Array.isArray(data.lots)) rememberProductLots(data.item.id, data.lots);
    const resolved = resolveLotForCart(data);
    if (resolved.action === 'pick_lot') {
      const priced = { ...resolved.item, lots: data.lots };
      mergeProducts([priced]);
      posCatalogCache.upsert([priced], { source: 'meta' }).catch(() => {});
      return resolved;
    }
    if (resolved.action === 'add' && resolved.lotId) {
      const priced = {
        ...resolved.item,
        lots: Array.isArray(data.lots) ? data.lots : resolved.item.lots,
        price: resolved.unitPrice,
        mrp: resolved.mrp ?? resolved.item.mrp,
        stock: resolved.lotQty,
        priceSource: 'lot',
      };
      mergeProducts([priced]);
      posCatalogCache.upsert([priced], { source: 'lot' }).catch(() => {});
      return resolved;
    }
    mergeProducts([data.item]);
    posCatalogCache.upsert([data.item], { source: 'meta' }).catch(() => {});
    return resolved;
  };

  const lineAllowsShortLots = (line) => (
    line.soldBy === SOLD_BY.WEIGHT || line.catalogComplete === false
  );

  /** @param {'auto'|'user'} [choice] auto = system attached unique price; user = cashier picked */
  const applyLotFieldsToLine = (line, lot, { choice = 'auto' } = {}) => {
    const oldKey = line.lineKey;
    const newKey = cartLineKey(line.id, lot.lotId);
    return {
      line: {
        ...line,
        lineKey: newKey,
        lotId: lot.lotId,
        requiresLot: true,
        lotChoice: choice,
        price: Number(lot.sellingPrice),
        mrp: lot.mrp ?? line.mrp,
        stock: Number(lot.qty),
      },
      oldKey,
      newKey,
    };
  };

  const applyQtyDraftRemaps = (remaps) => {
    if (!remaps?.length) return;
    setQtyDrafts((prev) => {
      let next = prev;
      let changed = false;
      remaps.forEach(({ oldKey, newKey }) => {
        if (!oldKey || !newKey || oldKey === newKey || next[oldKey] === undefined) return;
        if (!changed) {
          next = { ...prev };
          changed = true;
        }
        next[newKey] = next[oldKey];
        delete next[oldKey];
      });
      return changed ? next : prev;
    });
  };

  const upsertLotPricedCatalog = (line, lot) => {
    const priced = {
      id: line.id,
      name: line.name,
      sku: line.sku,
      status: 'ACTIVE',
      soldBy: line.soldBy,
      unit: line.unit,
      gstRate: line.gstRate,
      price: Number(lot.sellingPrice),
      mrp: lot.mrp ?? line.mrp,
      stock: Number(lot.qty),
      catalogComplete: line.catalogComplete,
      onboardSource: line.onboardSource,
      qtyStep: line.qtyStep,
      minOrderQty: line.minOrderQty,
      maxOrderQty: line.maxOrderQty,
      priceSource: 'lot',
    };
    mergeProducts([priced]);
    posCatalogCache.upsert([priced], { source: 'lot' }).catch(() => {});
  };

  /**
   * Refresh lot prices / attach unique-price lots before charge or amend.
   * @returns {{ status: 'ready'|'needs_pick'|'offline', cart: object[], pick?: object, draftRemaps?: object[] }}
   */
  const reconcileCartLots = async (sourceCart) => {
    const working = (sourceCart || []).map((line) => ({ ...line }));
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      const draftRemaps = [];
      const stamped = working.map((line) => {
        if (line.lotId) return line;
        const product = products.find((item) => item.id === line.id);
        const lot = defaultOpenLot(product?.lots, line.qty, { allowShortLots: lineAllowsShortLots(line) });
        if (!lot?.lotId) return line;
        const applied = applyLotFieldsToLine(line, lot, { choice: 'auto' });
        if (applied.oldKey !== applied.newKey) draftRemaps.push({ oldKey: applied.oldKey, newKey: applied.newKey });
        return applied.line;
      });
      return { status: 'offline', cart: stamped, draftRemaps };
    }
    if (!user?.idToken) {
      return { status: 'ready', cart: working, draftRemaps: [] };
    }

    const itemIds = [...new Set(working.map((line) => line.id).filter(Boolean))];
    const lotsByItem = new Map();
    await Promise.all(itemIds.map(async (itemId) => {
      try {
        const { lots } = await listLots(itemId, user.idToken);
        const nextLots = Array.isArray(lots) ? lots : [];
        lotsByItem.set(itemId, nextLots);
        rememberProductLots(itemId, nextLots);
      } catch {
        lotsByItem.set(itemId, null);
      }
    }));

    const draftRemaps = [];
    const markConfirmed = (lines) => lines.map((line) => (
      lotsByItem.get(line.id) != null ? { ...line, priceUnconfirmed: false } : line
    ));

    for (let i = 0; i < working.length; i += 1) {
      let line = working[i];
      const lots = lotsByItem.get(line.id);
      if (lots == null) continue;

      if (line.lotId) {
        const match = lots.find((lot) => lot.lotId === line.lotId);
        if (match && Number(match.qty) > 0) {
          const price = Number(match.sellingPrice);
          const stock = Number(match.qty);
          working[i] = {
            ...line,
            price: Number.isFinite(price) ? price : line.price,
            mrp: match.mrp ?? line.mrp,
            stock: Number.isFinite(stock) ? stock : line.stock,
          };
          continue;
        }
        const clearedKey = cartLineKey(line.id, null);
        if (line.lineKey !== clearedKey) {
          draftRemaps.push({ oldKey: line.lineKey, newKey: clearedKey });
        }
        line = {
          ...line,
          lotId: null,
          requiresLot: false,
          lotChoice: undefined,
          lineKey: clearedKey,
        };
        working[i] = line;
      }

      const decision = resolveOpenLotsForLine({
        lots,
        lineQty: line.qty,
        allowShortLots: lineAllowsShortLots(line),
      });

      if (decision.action === 'none') {
        const metaKey = cartLineKey(line.id, null);
        if (line.lineKey !== metaKey) {
          draftRemaps.push({ oldKey: line.lineKey, newKey: metaKey });
        }
        working[i] = {
          ...line,
          lotId: null,
          requiresLot: false,
          lotChoice: undefined,
          lineKey: metaKey,
        };
        continue;
      }

      const lot = decision.action === 'attach' ? decision.lot : null;
      if (decision.action === 'pick') {
        return {
          status: 'needs_pick',
          cart: markConfirmed(working),
          draftRemaps,
          pick: {
            item: {
              id: line.id,
              name: line.name,
              sku: line.sku,
              unit: line.unit,
              soldBy: line.soldBy,
              emoji: line.emoji,
              swatch: line.swatch,
              gstRate: line.gstRate,
              catalogComplete: line.catalogComplete,
              qtyStep: line.qtyStep,
              minOrderQty: line.minOrderQty,
              maxOrderQty: line.maxOrderQty,
            },
            lots: decision.lots,
            cartLineKey: line.lineKey,
          },
        };
      }
      if (!lot) continue;
      const { line: nextLine, oldKey, newKey } = applyLotFieldsToLine(line, lot, { choice: 'auto' });
      working[i] = nextLine;
      if (oldKey !== newKey) draftRemaps.push({ oldKey, newKey });
      upsertLotPricedCatalog(nextLine, lot);
    }

    return { status: 'ready', cart: markConfirmed(working), draftRemaps };
  };

  const cartItemsPayloadFrom = (lines) => (lines || []).map((line) => {
    const qty = line.soldBy === SOLD_BY.WEIGHT ? quantizeKg(line.qty) : line.qty;
    const item = { item_id: line.id, qty };
    if (line.lotId) item.lot_id = line.lotId;
    return item;
  });

  /** Cash To-pay for a cart snapshot (nearest ₹1), using the current unlocked coupon. */
  const cashDueForCart = (lines) => {
    const merch = (lines || []).reduce((sum, line) => sum + lineAmount(line.price, line.qty), 0);
    const afterCoupon = Math.max(0, merch - (appliedCoupon?.discountAmount || 0));
    return payableForTender(afterCoupon, 'CASH').payable;
  };

  /**
   * If the cashier typed exact cash for the old To-pay, lift it when reconcile bumps the price
   * so Charge stays one click after a mid-sale stock-in / reprice.
   * @returns {number} cash amount to use for the short-tender check
   */
  const liftExactCashAfterPriceBump = (priorDue, nextDue, currentCash) => {
    const cash = Number(currentCash) || 0;
    const prior = Number(priorDue) || 0;
    const next = Number(nextDue) || 0;
    if (!(next > cash) || Math.abs(cash - prior) >= 0.005) return cash;
    setPaymentAmounts((prev) => ({ ...prev, CASH: String(next) }));
    return next;
  };

  const applyReconcileResult = (result, { priorDue, cashMethod = false } = {}) => {
    applyQtyDraftRemaps(result.draftRemaps);
    setCart(result.cart);
    let cashForCheck = Number(paymentAmounts.CASH) || 0;
    if (cashMethod && result.status === 'ready') {
      const nextDue = cashDueForCart(result.cart);
      cashForCheck = liftExactCashAfterPriceBump(priorDue, nextDue, cashForCheck);
    }
    return { ...result, cashForCheck, nextDue: cashDueForCart(result.cart) };
  };

  const revalidateInBackground = (product) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const token = user?.idToken;
    if (!token || !product?.id) return;
    (async () => {
      try {
        let lots = null;
        if (product.barcode) {
          const data = await lookupByBarcode(product.barcode, token);
          if (data.item.status !== 'ACTIVE') {
            mergeProducts([data.item]);
            await posCatalogCache.upsert([data.item], { source: 'meta' });
            return;
          }
          await persistResolvedLookup(data);
          lots = Array.isArray(data.lots) ? data.lots : null;
          if (!lots) {
            try {
              const listed = await listLots(product.id, token);
              lots = listed.lots;
            } catch {
              lots = [];
            }
          }
        } else {
          try {
            const listed = await listLots(product.id, token);
            lots = listed.lots;
          } catch (err) {
            if (err.status === 404 || err.message === 'not_found') lots = [];
            else throw err;
          }
          const open = Array.isArray(lots) ? lots.filter((l) => Number(l.qty) > 0) : [];
          const cachedLot = defaultOpenLot(open, 1, {
            allowShortLots: product.soldBy === SOLD_BY.WEIGHT || product.catalogComplete === false,
          });
          if (cachedLot) {
            await posCatalogCache.upsert([{
              ...product,
              lots: open,
              price: cachedLot.sellingPrice,
              mrp: cachedLot.mrp ?? product.mrp,
              stock: cachedLot.qty,
            }], { source: 'lot' });
          } else {
            await posCatalogCache.upsert([{ ...product, lots: open }], { source: 'meta' });
          }
        }

        if (!Array.isArray(lots)) return;

        const remaps = [];
        let pickTarget = null;
        setCart((prev) => prev.map((line) => {
          if (line.id !== product.id) return line;

          if (line.lotId) {
            const match = lots.find((lot) => lot.lotId === line.lotId);
            if (match && Number(match.qty) > 0) {
              const price = Number(match.sellingPrice);
              const stock = Number(match.qty);
              return {
                ...line,
                price: Number.isFinite(price) ? price : line.price,
                mrp: match.mrp ?? line.mrp,
                stock: Number.isFinite(stock) ? stock : line.stock,
              };
            }
            const clearedKey = cartLineKey(line.id, null);
            if (line.lineKey !== clearedKey) remaps.push({ oldKey: line.lineKey, newKey: clearedKey });
            line = {
              ...line,
              lotId: null,
              requiresLot: false,
              lotChoice: undefined,
              lineKey: clearedKey,
            };
          }

          const decision = resolveOpenLotsForLine({
            lots,
            lineQty: line.qty,
            allowShortLots: lineAllowsShortLots(line),
          });

          if (decision.action === 'pick') {
            if (!pickTarget) {
              pickTarget = {
                item: {
                  id: line.id,
                  name: line.name,
                  sku: line.sku,
                  unit: line.unit,
                  soldBy: line.soldBy,
                  catalogComplete: line.catalogComplete,
                },
                lots: decision.lots,
                cartLineKey: line.lineKey,
              };
            }
            return line;
          }

          const lot = decision.action === 'attach' ? decision.lot : null;
          if (!lot) return line;
          const { line: next, oldKey, newKey } = applyLotFieldsToLine(line, lot, { choice: 'auto' });
          if (oldKey !== newKey) remaps.push({ oldKey, newKey });
          upsertLotPricedCatalog(next, lot);
          return next;
        }));
        if (pickTarget) {
          setLotPick((prev) => (prev?.item?.id === pickTarget.item.id ? prev : pickTarget));
        }
        applyQtyDraftRemaps(remaps);
      } catch {
        // Keep the last-known cached row and cart line.
      }
    })();
  };

  const qtyAlreadyOnLots = (itemId, lots, lines) => {
    const taken = {};
    (lines || []).forEach((line) => {
      if (line.id !== itemId || !line.lotId) return;
      taken[line.lotId] = (taken[line.lotId] || 0) + Number(line.qty || 0);
    });
    (lots || []).forEach((lot) => {
      const reserved = isEditMode ? 0 : reservedQty(offlineSnap.charges, itemId, lot.lotId);
      if (reserved) taken[lot.lotId] = (taken[lot.lotId] || 0) + reserved;
    });
    return taken;
  };

  const placeOfflinePieces = (product, baseLines, needQty, { keepValue = false } = {}) => {
    const lots = product?.lots;
    if (!Array.isArray(lots) || !lots.length) return false;
    const { chunks, short } = allocateOfflineQty(
      lots,
      qtyAlreadyOnLots(product.id, lots, baseLines),
      needQty,
    );
    if (!chunks.length) {
      setScanError(`${product.name} is out of stock`);
      return true;
    }
    let next = baseLines.map((line) => ({ ...line }));
    chunks.forEach((chunk) => {
      const key = cartLineKey(product.id, chunk.lot.lotId);
      const existing = next.find((line) => line.lineKey === key);
      if (existing) {
        const fromQty = existing.qty;
        const summed = Number(existing.qty) + chunk.qty;
        existing.qty = isSoldByWeight(product) ? (quantizeKg(summed) ?? summed) : summed;
        pushQtyChange({ line: { ...existing }, fromQty, toQty: existing.qty });
        return;
      }
      const created = {
        lineKey: key,
        id: product.id,
        lotId: chunk.lot.lotId,
        requiresLot: true,
        lotChoice: 'auto',
        name: product.name,
        sku: product.sku,
        price: Number(chunk.lot.sellingPrice),
        mrp: chunk.lot.mrp ?? product.mrp,
        unit: product.unit,
        soldBy: isSoldByWeight(product) ? SOLD_BY.WEIGHT : SOLD_BY.EACH,
        qtyStep: product.qtyStep,
        minOrderQty: product.minOrderQty,
        maxOrderQty: product.maxOrderQty,
        emoji: product.emoji,
        swatch: product.swatch,
        stock: Number(chunk.lot.qty),
        gstRate: product.gstRate || 0,
        catalogComplete: product.catalogComplete,
        onboardSource: product.onboardSource,
        priceUnconfirmed: true,
        qty: chunk.qty,
      };
      next = [created, ...next];
      pushQtyChange({ line: created, fromQty: null, toQty: chunk.qty });
    });
    setCart(next);
    holdPaymentForMinOrder(next);
    setFlashId(cartLineKey(product.id, chunks[chunks.length - 1].lot.lotId));
    setTimeout(() => setFlashId((id) => (id === cartLineKey(product.id, chunks[chunks.length - 1].lot.lotId) ? null : id)), 900);
    if (!keepValue) setScanValue('');
    setScanError(short > 0 ? `${product.name} is out of stock` : '');
    return true;
  };

  const addCachedProduct = async (product, { keepValue } = {}) => {
    if (product.status && product.status !== 'ACTIVE') {
      setScanError(`${product.name} is not available for sale`);
      return;
    }

    // Tile / cached barcode used to add META-only and hope background revalidate
    // opened the picker. With two sell prices (e.g. ₹40 + ₹60) that often never
    // showed — resolve lots first so the pack picker opens before the line is added.
    if (typeof navigator === 'undefined' || navigator.onLine !== false) {
      if (user?.idToken) {
        try {
          const resolved = await resolveProductWithLots(product);
          applyResolvedLot(resolved, { keepValue });
          revalidateInBackground(product);
          return;
        } catch {
          // Fall through to offline-style cache add.
        }
      }
    }

    const cachedLot = defaultOpenLot(product.lots, 1, {
      allowShortLots: isSoldByWeight(product) || product.catalogComplete === false,
    });
    if (isSoldByWeight(product)) {
      const lots = Array.isArray(product.lots) ? product.lots : [];
      const allocationTaken = lots.length ? qtyAlreadyOnLots(product.id, lots, cart) : null;
      const remainingKg = lots.length
        ? (quantizeKg(lots.reduce((sum, lot) => {
          const room = Number(lot.qty) - (Number(allocationTaken?.[lot.lotId]) || 0);
          return sum + (room > 0 ? room : 0);
        }, 0)) ?? 0)
        : null;
      if (lots.length && !(remainingKg > 0)) {
        setScanError(`${product.name} is out of stock`);
        if (!keepValue) setScanValue('');
        revalidateInBackground(product);
        return;
      }
      openWeighSheet({
        item: product,
        unitPrice: cachedLot ? Number(cachedLot.sellingPrice) : product.price,
        lotQty: lots.length ? remainingKg : (cachedLot ? Number(cachedLot.qty) : product.stock),
        mrp: cachedLot?.mrp ?? product.mrp,
        lotId: cachedLot?.lotId || null,
        requiresLot: Boolean(cachedLot),
        keepValue,
        mode: 'add',
        fromCache: true,
        spillLots: lots.length > 0,
        allocationLots: lots.length ? lots : null,
        allocationTaken,
      });
      revalidateInBackground(product);
      return;
    }
    if (Array.isArray(product.lots) && product.lots.length) {
      placeOfflinePieces(product, cart, 1, { keepValue });
      revalidateInBackground(product);
      return;
    }
    addToCart(product, {
      keepValue,
      unitPrice: cachedLot ? Number(cachedLot.sellingPrice) : product.price,
      lotQty: cachedLot ? Number(cachedLot.qty) : product.stock,
      mrp: cachedLot?.mrp ?? product.mrp,
      lotId: cachedLot?.lotId || null,
      requiresLot: Boolean(cachedLot),
      fromCache: true,
    });
    revalidateInBackground(product);
  };

  const resolveScannedCode = async (code) => {
    const normalized = String(code || '').trim();
    if (!normalized) return;

    const cachedBarcode = activeByBarcode.get(normalized);
    if (cachedBarcode) {
      addCachedProduct(cachedBarcode, { keepValue: true });
      return;
    }
    const inactiveBarcode = inactiveByBarcode.get(normalized);
    if (inactiveBarcode) {
      setScanError(`${inactiveBarcode.name} is not available for sale`);
      return;
    }

    // IndexedDB / in-cache barcode index before network.
    try {
      const fromDisk = await posCatalogCache.findByBarcode(normalized);
      if (fromDisk) {
        mergeProducts([fromDisk]);
        addCachedProduct(fromDisk, { keepValue: true });
        return;
      }
    } catch {
      // Continue to API / text fallback.
    }

    try {
      const data = await lookupByBarcode(normalized, user.idToken);
      if (String(data.item?.barcode || '').trim() !== normalized) {
        setScanError(`No product found for "${normalized}".`);
        return;
      }
      if (data.item.status !== 'ACTIVE') {
        mergeProducts([data.item]);
        posCatalogCache.upsert([data.item], { source: 'meta' }).catch(() => {});
        setScanError(`${data.item.name} is not available for sale`);
        return;
      }
      const resolved = persistResolvedLookup(data);
      applyResolvedLot(resolved, { keepValue: true });
      return;
    } catch (err) {
      const notFound = err.message === 'not_found' || err.status === 404;
      const networkFailed = !err.status && err.message !== 'not_found';
      if (networkFailed || !browserOnline) {
        setScanError('Not in catalog. Add it in Products.');
        return;
      }
      if (!notFound) {
        setScanError(err.message || 'Could not look up barcode.');
        return;
      }
    }

    const needle = normalized.toLowerCase();
    const matches = activeProducts.filter((product) => (
      product.name.toLowerCase().includes(needle)
      || product.sku.toLowerCase().includes(needle)
      || (product.barcode || '').includes(normalized)
    ));
    if (matches.length === 1) {
      addCachedProduct(matches[0], { keepValue: true });
      return;
    }
    if (matches.length > 1) { setScanError('Multiple matches — pick one below'); return; }
    if (browserOnline) {
      openInstantOnboard('EACH', normalized);
      return;
    }
    setScanError('Not in catalog. Add it in Products.');
  };

  const handleQuickAdd = (product, { keepValue } = {}) => {
    addCachedProduct(product, { keepValue });
  };

  useEffect(() => {
    if (!restoredLotsPending || !user?.idToken) return undefined;
    let cancelled = false;
    const lines = restoredLinesRef.current || [];
    (async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (!cancelled) setRestoredLotsPending(false);
        return;
      }
      for (const line of lines) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { lots } = await listLots(line.id, user.idToken);
          let next = null;
          if (line.lotId) {
            const match = (lots || []).find((lot) => lot.lotId === line.lotId);
            if (match && Number(match.qty) > 0) {
              next = {
                price: match.sellingPrice,
                mrp: match.mrp ?? line.mrp,
                stock: match.qty,
                lotId: match.lotId,
              };
            }
          }
          if (!next) {
            const lot = defaultOpenLot(lots, line.qty, { allowShortLots: lineAllowsShortLots(line) });
            if (lot) {
              next = {
                price: lot.sellingPrice,
                mrp: lot.mrp ?? line.mrp,
                stock: lot.qty,
                lotId: lot.lotId,
              };
            }
          }
          if (!next || cancelled) continue;
          const fromLot = Boolean(next.lotId);
          // eslint-disable-next-line no-await-in-loop
          await posCatalogCache.upsert([{
            id: line.id,
            name: line.name,
            sku: line.sku,
            status: 'ACTIVE',
            soldBy: line.soldBy,
            unit: line.unit,
            gstRate: line.gstRate,
            price: next.price,
            mrp: next.mrp,
            stock: next.stock,
            catalogComplete: line.catalogComplete,
            onboardSource: line.onboardSource,
            qtyStep: line.qtyStep,
            minOrderQty: line.minOrderQty,
            maxOrderQty: line.maxOrderQty,
          }], { source: fromLot ? 'lot' : 'meta' });
          let draftRemap = null;
          setCart((prev) => prev.map((entry) => {
            if (entry.lineKey !== line.lineKey) return entry;
            const price = Number(next.price);
            const stock = Number(next.stock);
            const nextKey = next.lotId ? cartLineKey(entry.id, next.lotId) : cartLineKey(entry.id, null);
            if (entry.lineKey !== nextKey) {
              draftRemap = { oldKey: entry.lineKey, newKey: nextKey };
            }
            return {
              ...entry,
              price: Number.isFinite(price) ? price : entry.price,
              mrp: next.mrp ?? entry.mrp,
              stock: Number.isFinite(stock) ? stock : entry.stock,
              lotId: next.lotId || null,
              requiresLot: Boolean(next.lotId),
              lotChoice: next.lotId ? (entry.lotChoice === 'user' ? 'user' : 'auto') : undefined,
              lineKey: nextKey,
            };
          }));
          if (draftRemap) applyQtyDraftRemaps([draftRemap]);
        } catch {
          // Leave the restored line in place.
        }
      }
      if (!cancelled) setRestoredLotsPending(false);
    })();
    return () => { cancelled = true; };
  }, [restoredLotsPending, user?.idToken]);

  const drainScanQueue = async () => {
    if (processingScanRef.current) return;
    processingScanRef.current = true;
    setScanning(true);
    setScanError('');
    try {
      while (scanQueueRef.current.length > 0) {
        const code = scanQueueRef.current.shift();
        await resolveScannedCode(code);
      }
    } finally {
      processingScanRef.current = false;
      setScanning(false);
      setScanValue('');
      requestAnimationFrame(() => focusScanField());
    }
  };

  const handleScanKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const code = scanValue.trim();
    if (!code) return;
    e.preventDefault();
    setScanValue('');
    scanQueueRef.current.push(code);
    drainScanQueue();
  };

  const changeQty = (lineKey, delta) => {
    setQtyDrafts((prev) => {
      if (prev[lineKey] === undefined) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
    const line = cart.find((l) => l.lineKey === lineKey);
    if (!line || line.soldBy === SOLD_BY.WEIGHT) return;
    const nextQty = line.qty + delta;
    if (nextQty <= 0) {
      pushQtyChange({ line, fromQty: line.qty, toQty: null });
      const remaining = cart.filter((l) => l.lineKey !== lineKey);
      setCart(remaining);
      holdPaymentForMinOrder(remaining);
      return;
    }
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    const sellable = line.stock + stockCredit - (isEditMode ? 0 : reservedQty(offlineSnap.charges, line.id, line.lotId));
    if (line.catalogComplete !== false && nextQty > sellable) {
      if (!browserOnline && !isEditMode) {
        const product = products.find((item) => item.id === line.id);
        if (product && placeOfflinePieces(product, cart, delta, { keepValue: true })) return;
      }
      const left = Math.max(0, sellable);
      setScanError(left <= 0 ? `${line.name} is out of stock` : `Only ${left} of ${line.name} left`);
      return;
    }
    const qtyProblem = orderQtyProblem(nextQty, line);
    setScanError(qtyProblem ? `${line.name}: ${qtyProblem}` : '');
    pushQtyChange({ line: { ...line, qty: nextQty }, fromQty: line.qty, toQty: nextQty });
    const nextLines = cart.map((l) => (l.lineKey === lineKey ? { ...l, qty: nextQty } : l));
    setCart(nextLines);
    holdPaymentForMinOrder(nextLines);
  };

  const setQty = (lineKey, rawQty) => {
    const line = cart.find((l) => l.lineKey === lineKey);
    if (!line || line.soldBy === SOLD_BY.WEIGHT) return;
    const parsed = Math.floor(Number(rawQty));
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    const stockCredit = isEditMode ? (Number(amendStockCredit[lineKey]) || 0) : 0;
    const sellable = line.catalogComplete !== false
      ? line.stock + stockCredit - (isEditMode ? 0 : reservedQty(offlineSnap.charges, line.id, line.lotId))
      : Number.POSITIVE_INFINITY;
    if (parsed > sellable) {
      if (!browserOnline && !isEditMode) {
        const product = products.find((item) => item.id === line.id);
        if (product?.lots?.length) {
          const stay = Math.min(parsed, Math.max(0, sellable));
          const base = stay === line.qty
            ? cart
            : cart.map((entry) => (entry.lineKey === line.lineKey ? { ...entry, qty: stay } : entry));
          if (stay !== line.qty) {
            pushQtyChange({ line: { ...line, qty: stay }, fromQty: line.qty, toQty: stay });
          }
          const spill = parsed - stay;
          if (spill > 0) placeOfflinePieces(product, base, spill, { keepValue: true });
          else {
            setCart(base);
            holdPaymentForMinOrder(base);
            setScanError('');
          }
          return;
        }
      }
      const left = Math.max(0, sellable);
      setScanError(left <= 0 ? `${line.name} is out of stock` : `Only ${left} of ${line.name} left`);
      return;
    }
    const nextQty = parsed;
    if (nextQty <= 0 || nextQty === line.qty) return;
    const qtyProblem = orderQtyProblem(nextQty, line);
    setScanError(qtyProblem ? `${line.name}: ${qtyProblem}` : '');
    pushQtyChange({ line: { ...line, qty: nextQty }, fromQty: line.qty, toQty: nextQty });
    const nextLines = cart.map((l) => (l.lineKey === lineKey ? { ...l, qty: nextQty } : l));
    setCart(nextLines);
    holdPaymentForMinOrder(nextLines);
  };

  const editWeightLine = (line) => {
    const product = products.find((item) => item.id === line.id);
    const lots = Array.isArray(product?.lots) ? product.lots : [];
    const canSpill = !browserOnline && !isEditMode && lots.length > 1;
    if (!canSpill) {
      openWeighSheet({
        item: {
          id: line.id,
          name: line.name,
          price: line.price,
          unit: line.unit,
          stock: line.stock,
          soldBy: SOLD_BY.WEIGHT,
          qtyStep: line.qtyStep,
          minOrderQty: line.minOrderQty,
          maxOrderQty: line.maxOrderQty,
          emoji: line.emoji,
          swatch: line.swatch,
        },
        unitPrice: line.price,
        lotQty: line.stock,
        mrp: line.mrp,
        requiresLot: line.requiresLot,
        lotId: line.lotId,
        mode: 'edit',
        initialWeight: line.qty,
      });
      return;
    }
    const taken = qtyAlreadyOnLots(line.id, lots, cart);
    const thisQty = Number(line.qty) || 0;
    const takenWithout = {
      ...taken,
      [line.lotId]: Math.max(0, (Number(taken[line.lotId]) || 0) - thisQty),
    };
    const thisLot = lots.find((lot) => lot.lotId === line.lotId);
    const thisRoom = quantizeKg(
      Number(thisLot?.qty ?? line.stock) - (Number(takenWithout[line.lotId]) || 0),
    ) ?? 0;
    const totalRoom = quantizeKg(lots.reduce((sum, lot) => {
      const room = Number(lot.qty) - (Number(takenWithout[lot.lotId]) || 0);
      return sum + (room > 0 ? room : 0);
    }, 0)) ?? 0;
    openWeighSheet({
      item: {
        id: line.id,
        name: line.name,
        price: line.price,
        unit: line.unit,
        stock: line.stock,
        soldBy: SOLD_BY.WEIGHT,
        qtyStep: line.qtyStep,
        minOrderQty: line.minOrderQty,
        maxOrderQty: line.maxOrderQty,
        emoji: line.emoji,
        swatch: line.swatch,
        lots,
      },
      unitPrice: line.price,
      lotQty: totalRoom,
      mrp: line.mrp,
      requiresLot: line.requiresLot,
      lotId: line.lotId,
      mode: 'edit',
      initialWeight: line.qty,
      spillLots: true,
      allocationLots: lots,
      allocationTaken: takenWithout,
      editLotCap: thisRoom,
    });
  };

  const removeLine = (lineKey) => {
    const line = cart.find((l) => l.lineKey === lineKey);
    if (line) pushQtyChange({ line, fromQty: line.qty, toQty: null });
    setCart((prev) => prev.filter((l) => l.lineKey !== lineKey));
    setQtyDrafts((prev) => {
      if (prev[lineKey] === undefined) return prev;
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
  };

  const undoLastAdd = () => {
    const stack = undoStackRef.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    applyQtyEntry(entry, 'undo');
    commitUndoStack(stack.slice(0, -1));
    commitRedoStack([...redoStackRef.current, entry]);
  };

  const redoLastUndo = () => {
    const stack = redoStackRef.current;
    if (!stack.length) return;
    const entry = stack[stack.length - 1];
    applyQtyEntry(entry, 'redo');
    commitRedoStack(stack.slice(0, -1));
    commitUndoStack([...undoStackRef.current, entry]);
  };

  const clearCart = () => {
    setCart([]);
    setCartSearch('');
    setCustomerPhone('');
    clearEditHistory();
    setQtyDrafts({});
    setLotPick(null);
    setWeighPick(null);
    if (isEditMode) {
      setChargeError(POS_CHARGE_ERRORS.items_required);
    }
  };

  const cartQuery = cartSearch.trim().toLowerCase();
  const visibleCartLines = useMemo(() => {
    if (!cartQuery) return cart;
    return cart.filter((l) => (
      l.name.toLowerCase().includes(cartQuery) || (l.sku || '').toLowerCase().includes(cartQuery)
    ));
  }, [cart, cartQuery]);

  const subtotal = useMemo(() => cart.reduce((sum, l) => sum + lineAmount(l.price, l.qty), 0), [cart]);

  const discount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  /** Merchandise total (subtotal − discount) — unrounded; lines stay at paise. */
  const merchandiseTotal = Math.max(0, subtotal - discount);
  const total = merchandiseTotal;

  const prevSubtotalRef = useRef(subtotal);
  useEffect(() => {
    if (editSale?.couponLocked) {
      prevSubtotalRef.current = subtotal;
      return;
    }
    if (appliedCoupon && prevSubtotalRef.current !== subtotal) {
      setAppliedCoupon(null);
      setCouponError('Cart changed — re-apply the coupon to confirm the discount.');
    }
    prevSubtotalRef.current = subtotal;
  }, [subtotal, appliedCoupon, editSale?.couponLocked]);

  const isSplit = selectedMethods.length > 1;
  const singleMethod = selectedMethods.length === 1 ? selectedMethods[0] : null;
  /** CASH preview: nearest ₹1 half-up. UPI/CARD: exact. Split: no FE cash round. */
  const tenderForRound = !isEditMode && !isSplit && singleMethod ? singleMethod : null;
  const { payable: payableTotal, round_off: previewRoundOff } = useMemo(
    () => payableForTender(merchandiseTotal, tenderForRound || 'UPI'),
    [merchandiseTotal, tenderForRound],
  );
  const editCashPayable = isEditMode && singleMethod === 'CASH'
    ? payableForTender(merchandiseTotal, 'CASH').payable
    : null;
  const cashAmount = Number(paymentAmounts.CASH) || 0;
  const paymentTotal = useMemo(
    () => selectedMethods.reduce((sum, m) => sum + (Number(paymentAmounts[m]) || 0), 0),
    [selectedMethods, paymentAmounts],
  );
  const chargeDue = (!isEditMode && !isSplit && singleMethod === 'CASH') ? payableTotal : total;
  const balanceDue = isSplit ? Math.max(0, total - paymentTotal) : 0;
  const cashTarget = editCashPayable != null ? editCashPayable : chargeDue;
  const computedChangeDue = isSplit
    ? (paymentTotal >= total ? paymentTotal - total : 0)
    : (singleMethod === 'CASH' ? Math.max(0, cashAmount - cashTarget) : 0);
  const normalizedCounter = counterNumber.trim().toUpperCase();
  const counterIsValid = /^[A-Z0-9]{1,16}$/.test(normalizedCounter);
  const isPayLater = !isEditMode && singleMethod === 'PAY_LATER';
  const unsyncedCharges = offlineSnap.charges.filter((row) => !row.orderId && !row.conflict);
  const paidWaiting = unsyncedCharges.filter((row) => !isCreditCharge(row));
  const shownSaleCount = sessionStats.count + unsyncedCharges.length;
  const shownSaleTotal = sessionStats.total + unsyncedCharges.reduce(
    (sum, row) => sum + queuedBillAmount(row),
    0,
  );
  const qtyIssue = cartQtyIssue(cart);
  const stockIssue = isEditMode ? '' : cartStockIssue(cart, offlineSnap.charges);
  const priceUnconfirmed = cart.some((line) => line.priceUnconfirmed);
  // No-lot lines may pick up a sell price on Charge / tab-focus sync — keep Charge
  // clickable so reconcile can run even when cash still matches the stale META total.
  const cartNeedsLotSync = cart.some((line) => !line.lotId);
  const canCharge = !qtyIssue && !stockIssue && !isEditMode && !restoredLotsPending && counterIsValid && cart.length > 0 && !charging && !payLaterOpen && (
    isPayLater
      ? true
      : isSplit
        ? selectedMethods.length >= 2 && paymentTotal >= total
        : Boolean(singleMethod) && (
          singleMethod !== 'CASH' || cashAmount >= chargeDue || cartNeedsLotSync
        )
  );
  const canPreviewAmend = isEditMode
    && browserOnline
    && cart.length > 0
    && !charging
    && !amending
    && selectedMethods.length === 1;

  lotSyncStateRef.current = {
    cart,
    charging,
    payLaterOpen,
    lotPick,
    receipt,
    restoredLotsPending,
    idToken: user?.idToken,
    singleMethod,
    cashDueForCart,
    reconcileCartLots,
    applyReconcileResult,
  };

  // When the cashier returns from Stock In / another tab, refresh open-cart lot prices
  // so To pay matches live stock before Charge (no page reload).
  useEffect(() => {
    const syncOpenCartLots = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const s = lotSyncStateRef.current;
      if (lotSyncBusyRef.current || s.charging || s.payLaterOpen || s.lotPick?.instantEntry || s.receipt || s.restoredLotsPending) {
        return;
      }
      if (!s.idToken || (!s.cart.length && !s.lotPick)) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

      const dropPickerIfOnePrice = async () => {
        if (!s.lotPick || s.lotPick.instantEntry) return;
        const pickId = s.lotPick.item?.id;
        if (!pickId) {
          setLotPick(null);
          return;
        }
        try {
          const listed = await listLots(pickId, s.idToken);
          const decision = resolveOpenLotsForLine({
            lots: listed.lots,
            lineQty: 1,
            allowShortLots: s.lotPick.item?.soldBy === 'WEIGHT' || s.lotPick.item?.catalogComplete === false,
          });
          if (decision.action !== 'pick') setLotPick(null);
        } catch {
          // Leave the picker; the next scan can refresh it.
        }
      };

      lotSyncBusyRef.current = true;
      const sourceCart = s.cart;
      try {
        if (!sourceCart.length) {
          await dropPickerIfOnePrice();
          return;
        }
        const priorDue = s.cashDueForCart(sourceCart);
        const raw = await s.reconcileCartLots(sourceCart);
        const changed = raw.status === 'needs_pick' || raw.cart.some((line, index) => {
          const prev = sourceCart[index];
          if (!prev) return true;
          return prev.lineKey !== line.lineKey
            || prev.lotId !== line.lotId
            || Number(prev.price) !== Number(line.price)
            || Number(prev.mrp) !== Number(line.mrp)
            || Number(prev.stock) !== Number(line.stock)
            || Number(prev.qty) !== Number(line.qty);
        }) || raw.cart.length !== sourceCart.length;
        if (!changed) {
          await dropPickerIfOnePrice();
          return;
        }

        const result = s.applyReconcileResult(raw, {
          priorDue,
          cashMethod: s.singleMethod === 'CASH',
        });
        if (result.status === 'needs_pick') {
          setLotPick((prev) => (prev?.cartLineKey || prev?.instantEntry ? prev : {
            item: result.pick.item,
            lots: result.pick.lots,
            cartLineKey: result.pick.cartLineKey,
          }));
        } else {
          await dropPickerIfOnePrice();
        }
        setChargeError((prev) => (
          prev && /^Not enough stock/i.test(prev) ? '' : prev
        ));
      } catch {
        // Keep the on-screen cart; Charge still reconciles.
      } finally {
        lotSyncBusyRef.current = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncOpenCartLots();
    };
    window.addEventListener('focus', syncOpenCartLots);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', syncOpenCartLots);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const confirmCounterSetup = (event) => {
    event.preventDefault();
    if (!normalizedCounter) {
      setCounterSetupError(POS_CHARGE_ERRORS.counter_required);
      return;
    }
    if (!counterIsValid) {
      setCounterSetupError(POS_CHARGE_ERRORS.invalid_counter);
      return;
    }
    setCounterNumber(normalizedCounter);
    setCounterSetupError('');
    setCounterSetupOpen(false);
    requestAnimationFrame(() => focusScanField());
  };

  const queueLinesFrom = (lines) => (lines || []).map((line) => ({
    item_id: line.id,
    qty: line.soldBy === SOLD_BY.WEIGHT ? quantizeKg(line.qty) : line.qty,
    lot_id: line.lotId || undefined,
    name: line.name,
    price: line.price,
    soldBy: line.soldBy,
    unit: line.unit,
    requiresLot: Boolean(line.requiresLot && !line.lotId),
  }));

  const saleAttemptFor = (saleCart, method) => {
    const fingerprint = `${method}|${(saleCart || []).map((line) => (
      `${line.id}:${line.qty}:${line.lotId || ''}`
    )).join(',')}`;
    const current = chargeAttemptRef.current;
    if (current?.posted) return current;
    if (!current || current.fingerprint !== fingerprint) {
      chargeAttemptRef.current = {
        id: newClientPaymentId(),
        at: new Date().toISOString(),
        fingerprint,
        posted: false,
      };
    }
    return chargeAttemptRef.current;
  };

  const finishQueuedCharge = async ({
    saleCart,
    method = 'PAY_LATER',
    customer = null,
    attempt,
    attempted = false,
    error = null,
  }) => {
    const paymentMethod = String(method || 'PAY_LATER').toUpperCase();
    const saleLines = (saleCart || []).map((line) => {
      if (line.lotId) return line;
      const product = products.find((item) => item.id === line.id);
      const lot = defaultOpenLot(product?.lots, line.qty, { allowShortLots: lineAllowsShortLots(line) });
      if (!lot?.lotId) return line;
      const price = Number(lot.sellingPrice);
      return {
        ...line,
        lotId: lot.lotId,
        requiresLot: true,
        price: Number.isFinite(price) ? price : line.price,
        mrp: lot.mrp ?? line.mrp,
      };
    });
    const needsLot = saleLines.some((line) => line.requiresLot && !line.lotId);
    if (!attempted && !isEditMode) {
      const stockMessage = cartStockIssue(saleLines, offlineSnap.charges);
      if (stockMessage) {
        setChargeError(stockMessage);
        return;
      }
    }
    const merchandise = roundMoney(saleLines.reduce(
      (sum, line) => sum + lineAmount(line.price, line.qty),
      0,
    ));
    const discount = paymentMethod === 'PAY_LATER'
      ? roundMoney(appliedCoupon?.discountAmount || 0)
      : 0;
    const net = roundMoney(Math.max(0, merchandise - discount));
    const quote = paymentMethod === 'PAY_LATER'
      ? { payable: net }
      : payableForTender(net, paymentMethod);
    const row = await posOfflineQueue.enqueueCharge({
      paymentMethod,
      drawerAmount: paymentMethod === 'CASH' ? quote.payable : 0,
      needsLot,
      clientSaleId: attempt.id,
      chargedAt: attempt.at,
      counter: normalizedCounter,
      items: queueLinesFrom(saleLines),
      customerName: customer?.fullName || '',
      customerPhone: customer?.phone || (phoneDigits(customerPhone).length === 10 ? phoneDigits(customerPhone) : ''),
      addressLine1: customer?.addressLine1 || '',
      addressLine2: customer?.addressLine2 || '',
      block: customer?.block || '',
      flat: customer?.flat || '',
      couponCode: paymentMethod === 'PAY_LATER' ? (appliedCoupon?.code || null) : null,
      discountAmount: discount,
      paidNow: attempted && customer ? customer.paidNow : null,
      paidNowMethod: attempted && customer ? customer.paidNowMethod : null,
      clientPaymentId: attempted && customer ? customer.clientPaymentId : null,
      paidNowAt: attempted && customer ? customer.settledAt : null,
      attempted,
      errorCode: error?.code || null,
      errorMessage: error ? mapPosChargeError(error) : null,
      errorItemId: error?.body?.item_id || null,
      cashierName: user?.name || '',
      customerNumber: allocatePosCustomerNumber(),
    });
    const slip = buildProvisionalReceipt(row, { cashierName: user?.name });
    const change = paymentMethod === 'CASH' ? Math.max(0, cashAmount - quote.payable) : 0;
    resetCheckout();
    chargeAttemptRef.current = null;
    setPayLaterOpen(false);
    setPayLaterLotNote(false);
    setChangeDue(change);
    setReceipt(slip);
    setLastReceipt(slip);
    const label = paymentMethod === 'PAY_LATER' ? 'Pay Later' : paymentMethod;
    const lotNote = needsLot ? ' Lot will be picked when this bill syncs.' : '';
    notify(paymentMethod === 'PAY_LATER'
      ? `Pay Later queued · ${slip.display_id} · ${formatCurrency(slip.total)} due.${lotNote}`
      : `${label} queued · ${slip.display_id} · ${formatCurrency(slip.total)}.${lotNote}`);
  };

  const finishQueuedPayLater = (args) => finishQueuedCharge({ ...args, method: 'PAY_LATER' });

  const openPayLaterModal = async () => {
    if (isEditMode || charging) return;
    if (!cart.length) {
      setChargeError('Add items before Pay Later.');
      return;
    }
    if (qtyIssue) {
      setChargeError(qtyIssue);
      return;
    }
    if (stockIssue) {
      setChargeError(stockIssue);
      return;
    }
    if (!normalizedCounter || !counterIsValid) {
      setChargeError(normalizedCounter ? POS_CHARGE_ERRORS.invalid_counter : POS_CHARGE_ERRORS.counter_required);
      return;
    }
    setChargeError('');
    const needsLot = cart.some((line) => line.requiresLot && !line.lotId);
    if (!browserOnline) {
      setPayLaterLotNote(needsLot);
      setPayLaterOpen(true);
      return;
    }
    setCharging(true);
    try {
      const priorDue = cashDueForCart(cart);
      const raw = await reconcileCartLots(cart);
      if (raw.status === 'offline') {
        setPayLaterLotNote(needsLot);
        setPayLaterOpen(true);
        return;
      }
      const result = applyReconcileResult(raw, { priorDue, cashMethod: false });
      if (result.status === 'needs_pick') {
        setLotPick({
          item: result.pick.item,
          lots: result.pick.lots,
          cartLineKey: result.pick.cartLineKey,
          resume: 'pay_later',
        });
        return;
      }
      setPayLaterLotNote(result.cart.some((line) => line.requiresLot && !line.lotId));
      setPayLaterOpen(true);
    } catch (err) {
      setChargeError(err?.message || 'Could not refresh cart lots.');
    } finally {
      setCharging(false);
    }
  };

  const togglePaymentMethod = (key) => {
    if (holdPaymentForMinOrder(cart)) return;
    setChargeError('');
    // Bill edit: single tender only (amend API takes one payment_method).
    if (isEditMode) {
      setSelectedMethods([key]);
      return;
    }
    // Pay Later is exclusive — never split with Cash/UPI/Card.
    if (key === 'PAY_LATER') {
      if (selectedMethods.includes('PAY_LATER')) {
        setSelectedMethods([]);
        setPaymentAmounts({});
        setPayLaterOpen(false);
        return;
      }
      setSelectedMethods(['PAY_LATER']);
      setPaymentAmounts({});
      // Open customer details immediately when Pay Later is chosen.
      openPayLaterModal();
      return;
    }
    if (selectedMethods.includes(key)) {
      setSelectedMethods((prev) => prev.filter((m) => m !== key));
      setPaymentAmounts((amounts) => {
        const next = { ...amounts };
        delete next[key];
        return next;
      });
      return;
    }
    // Selecting a cash tender clears Pay Later.
    setSelectedMethods((prev) => {
      const withoutPayLater = prev.filter((m) => m !== 'PAY_LATER');
      return [...withoutPayLater, key];
    });
    setPaymentAmounts((amounts) => {
      const next = { ...amounts };
      delete next.PAY_LATER;
      return next;
    });
  };

  const setPaymentAmount = (method, value) => {
    const raw = String(value).replace(/[^\d.]/g, '');
    const dot = raw.indexOf('.');
    const next = dot === -1
      ? raw
      : `${raw.slice(0, dot)}.${raw.slice(dot + 1).replace(/\./g, '').slice(0, 2)}`;
    setPaymentAmounts((prev) => ({ ...prev, [method]: next }));
  };

  const fillRemainingAmount = (method) => {
    const otherTotal = selectedMethods
      .filter((m) => m !== method)
      .reduce((sum, m) => sum + (Number(paymentAmounts[m]) || 0), 0);
    const remaining = Math.max(0, total - otherTotal);
    setPaymentAmount(method, remaining ? String(remaining) : '');
  };

  const resolvePaymentMethod = () => {
    if (!isSplit) return singleMethod;
    const nonCash = selectedMethods.find((m) => m !== 'CASH');
    if (nonCash) return nonCash;
    return selectedMethods.reduce((best, m) => (
      (Number(paymentAmounts[m]) || 0) > (Number(paymentAmounts[best]) || 0) ? m : best
    ), selectedMethods[0]);
  };

  const applyCoupon = async () => {
    if (!browserOnline && !isEditMode) {
      setCouponError('Back online to use a coupon.');
      return;
    }
    if (editSale?.couponLocked) {
      setCouponError(POS_CHARGE_ERRORS.coupon_locked);
      return;
    }
    const code = couponInput.trim().toUpperCase();
    if (!code || couponChecking) return;
    setCouponChecking(true);
    setCouponError('');
    try {
      const result = await validateCoupon({ couponCode: code, subtotal }, user.idToken);
      setAppliedCoupon({
        code: result.couponCode || code,
        discountAmount: result.discountAmount,
        discountPercent: result.discountPercent,
      });
      setCouponInput('');
    } catch (err) {
      setCouponError(COUPON_VALIDATION_ERRORS[err.message] || err.message || 'Could not validate this coupon.');
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    if (editSale?.couponLocked) {
      setCouponError(POS_CHARGE_ERRORS.coupon_locked);
      return;
    }
    setAppliedCoupon(null);
    setCouponError('');
  };

  const resetCheckout = () => {
    setChangeDue(0);
    setCart([]);
    clearEditHistory();
    setQtyDrafts({});
    setCustomerPhone('');
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
    setSelectedMethods([]);
    setPaymentAmounts({});
    setChargeError('');
    setCartSearch('');
    setAmendPreview(null);
    setAmendError('');
    setAmendStockCredit({});
    setEditSale(null);
    setRepreviewing(false);
  };

  const exitEditMode = () => {
    resetCheckout();
    setScanValue('');
    setScanError('');
    setLotPick(null);
    setWeighPick(null);
    focusScanField();
  };

  const beginEditSale = (salePayload) => {
    if (!canEditPosBills) {
      notify('You do not have permission to edit POS bills.');
      return;
    }
    if (salePayload?.voided) {
      notify('This bill is already voided — it cannot be amended.');
      return;
    }
    const orderId = salePayload?.order_id;
    if (!orderId) {
      notify('Could not open this bill for editing.');
      return;
    }
    const { cart: nextCart, credits } = buildCartFromSale(salePayload, products);
    if (!nextCart.length) {
      notify('This bill has no line items to edit.');
      return;
    }
    const couponCode = salePayload.coupon_code || null;
    const originalTender = String(salePayload.payment_method || '').toUpperCase();
    const knownTender = ['CASH', 'UPI', 'CARD', 'PAY_LATER'].includes(originalTender)
      ? originalTender
      : null;
    setEditSale({
      orderId,
      displayId: salePayload.display_id || orderId,
      couponLocked: Boolean(couponCode),
      couponCode,
      originalTotal: salePayload.total,
      originalAmountPaid: Number(salePayload.amount_paid ?? salePayload.amountPaid) || 0,
      originalPaymentMethod: knownTender,
      originalPhone: phoneDigits(
        salePayload.customer_phone
        || salePayload.customerPhone
        || salePayload.phone
        || '',
      ),
    });
    setAmendStockCredit(credits);
    setCart(nextCart);
    clearEditHistory();
    setQtyDrafts({});
    setCustomerPhone(phoneDigits(
      salePayload.customer_phone
      || salePayload.customerPhone
      || salePayload.phone
      || '',
    ));
    setAppliedCoupon(couponCode
      ? {
        code: couponCode,
        discountAmount: Number(salePayload.discount_amount) || 0,
        discountPercent: salePayload.discount_percent != null ? Number(salePayload.discount_percent) : undefined,
      }
      : null);
    setCouponInput('');
    setCouponError(couponCode ? 'Original coupon is locked on this bill.' : '');
    setSelectedMethods(knownTender ? [knownTender] : []);
    setPaymentAmounts({});
    setChargeError('');
    setCartSearch('');
    setAmendPreview(null);
    setAmendError('');
    setScanValue('');
    setScanError('');
    focusScanField();
  };

  const cartItemsPayload = () => cartItemsPayloadFrom(cart);

  const closeReceipt = () => {
    setReceipt(null);
    setChangeDue(0);
    setScanValue('');
    setScanError('');
    focusScanField();
  };

  const refreshFailedItem = async (err) => {
    const code = err?.code || err?.message;
    if (!STOCK_REFRESH_CODES.has(code)) return;
    const itemId = err?.body?.item_id || err?.body?.itemId;
    if (!itemId || !user?.idToken) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const product = await getProduct(itemId, user.idToken);
      let lots = [];
      try {
        const listed = await listLots(itemId, user.idToken);
        lots = Array.isArray(listed.lots) ? listed.lots : [];
      } catch {
        lots = [];
      }
      mergeProducts([product]);
      await posCatalogCache.upsert([product], { source: 'meta' });

      const remaps = [];
      setCart((prev) => prev.map((line) => {
        if (line.id !== itemId) return line;

        let working = line;
        if (working.lotId) {
          const match = lots.find((lot) => lot.lotId === working.lotId);
          if (match && Number(match.qty) > 0) {
            const price = Number(match.sellingPrice);
            const stock = Number(match.qty);
            return {
              ...working,
              price: Number.isFinite(price) ? price : working.price,
              mrp: match.mrp ?? working.mrp,
              stock: Number.isFinite(stock) ? stock : working.stock,
            };
          }
          const clearedKey = cartLineKey(working.id, null);
          if (working.lineKey !== clearedKey) {
            remaps.push({ oldKey: working.lineKey, newKey: clearedKey });
          }
          working = {
            ...working,
            lotId: null,
            requiresLot: false,
            lotChoice: undefined,
            lineKey: clearedKey,
          };
        }

        const decision = resolveOpenLotsForLine({
          lots,
          lineQty: working.qty,
          allowShortLots: lineAllowsShortLots(working),
        });

        const lot = decision.action === 'attach'
          ? decision.lot
          : defaultOpenLot(lots, working.qty, { allowShortLots: lineAllowsShortLots(working) });
        if (lot) {
          const { line: next, oldKey, newKey } = applyLotFieldsToLine(working, lot, { choice: 'auto' });
          if (oldKey !== newKey) remaps.push({ oldKey, newKey });
          upsertLotPricedCatalog(next, lot);
          return next;
        }

        return {
          ...working,
          stock: Number(product.stock) || working.stock,
        };
      }));
      applyQtyDraftRemaps(remaps);
    } catch {
      // Mapped charge/amend copy is still shown.
    }
  };

  const proceedCreatePosSale = async (saleCart, { payLaterCustomer, attempt: givenAttempt } = {}) => {
    const minErr = minOrderChargeError(saleCart);
    if (minErr) throw minErr;
    const change = computedChangeDue;
    const merchandise = saleCart.reduce((sum, line) => sum + lineAmount(line.price, line.qty), 0);
    const saleDiscount = appliedCoupon ? appliedCoupon.discountAmount : 0;
    const saleSubtotal = Math.max(0, merchandise);
    const isPayLaterSale = Boolean(payLaterCustomer)
      || String(resolvePaymentMethod() || '').toUpperCase() === 'PAY_LATER';
    const chargeBody = {
      items: cartItemsPayloadFrom(saleCart),
      payment_method: isPayLaterSale ? 'PAY_LATER' : resolvePaymentMethod(),
      counter: normalizedCounter,
      coupon_code: appliedCoupon?.code,
      discount_percent: 0,
      customer_phone: payLaterCustomer?.phone || customerPhone || undefined,
    };
    const paidNowAmount = Number(payLaterCustomer?.paidNow) || 0;
    const paidNowMethod = String(payLaterCustomer?.paidNowMethod || '').toUpperCase();
    if (payLaterCustomer) {
      chargeBody.customer_name = payLaterCustomer.fullName;
      chargeBody.customer_phone = payLaterCustomer.phone;
      chargeBody.address_line1 = payLaterCustomer.addressLine1;
      chargeBody.address_line2 = payLaterCustomer.addressLine2;
      chargeBody.block = payLaterCustomer.block;
      chargeBody.flat = payLaterCustomer.flat;
      if (paidNowAmount > 0) {
        chargeBody.paid_now = paidNowAmount;
        chargeBody.paid_now_method = paidNowMethod;
        if (payLaterCustomer.clientPaymentId) {
          chargeBody.client_payment_id = payLaterCustomer.clientPaymentId;
          chargeBody.settled_at = payLaterCustomer.settledAt;
          chargeBody.paid_at = payLaterCustomer.settledAt;
        }
      }
    }
    const attempt = givenAttempt
      || payLaterCustomer?.chargeAttempt
      || saleAttemptFor(saleCart, chargeBody.payment_method);
    chargeBody.client_sale_id = attempt.id;
    chargeBody.charged_at = attempt.at;
    if (chargeAttemptRef.current?.id === attempt.id) {
      chargeAttemptRef.current = { ...chargeAttemptRef.current, posted: true };
    }
    const data = await createPosSale(chargeBody, user.idToken);
    const saleReceipt = data?.receipt || data?.sale || data;

    const cartByItemId = new Map(saleCart.map((c) => [c.id || c.item_id, c]));
    const enrichedLineItems = (saleReceipt.line_items || []).map((li) => {
      const cartItem = cartByItemId.get(li.item_id || li.id);
      const mrp = Number(li.mrp ?? cartItem?.mrp);
      return {
        ...li,
        mrp: Number.isFinite(mrp) && mrp > 0 ? mrp : undefined,
      };
    });

    const confirmed = {
      ...saleReceipt,
      line_items: enrichedLineItems.length > 0 ? enrichedLineItems : (saleReceipt.line_items || []),
      counter: saleReceipt.counter || normalizedCounter,
      gstin: saleReceipt.gstin || POS_GSTIN,
      cashier_name: saleReceipt.cashier_name || user.name,
      customer_number: saleReceipt.customer_number,
      customer_phone: saleReceipt.customer_phone
        || (payLaterCustomer?.phone ? `+91${payLaterCustomer.phone}` : null)
        || (customerPhone ? `+91${customerPhone}` : undefined),
      customer_name: saleReceipt.customer_name || payLaterCustomer?.fullName || null,
      address_line1: saleReceipt.address_line1 || payLaterCustomer?.addressLine1 || null,
      address_line2: saleReceipt.address_line2 || payLaterCustomer?.addressLine2 || null,
      block: saleReceipt.block || payLaterCustomer?.block || null,
      flat: saleReceipt.flat || payLaterCustomer?.flat || null,
      payment_method: saleReceipt.payment_method || chargeBody.payment_method,
      payment_status: saleReceipt.payment_status
        || (isPayLaterSale ? 'PENDING' : undefined),
      amount_paid: saleReceipt.amount_paid != null
        ? Number(saleReceipt.amount_paid)
        : (isPayLaterSale ? paidNowAmount : undefined),
      outstanding: saleReceipt.outstanding != null
        ? Number(saleReceipt.outstanding)
        : (isPayLaterSale
          ? roundMoney(Math.max(0, Number(saleReceipt.total) - paidNowAmount))
          : undefined),
      payments: saleReceipt.payments,
      paid_by_method: saleReceipt.paid_by_method,
      tender_lines: isSplit
        ? selectedMethods
          .map((method) => ({
            method,
            amount: roundMoney(paymentAmounts[method]),
          }))
          .filter((line) => line.amount > 0)
        : undefined,
      round_off: saleReceipt.round_off ?? 0,
      discount_amount: saleReceipt.discount_amount ?? saleReceipt.discount ?? saleDiscount,
      discount_percent: saleReceipt.discount_percent
        ?? appliedCoupon?.discountPercent
        ?? (saleSubtotal > 0 && saleDiscount > 0 ? (saleDiscount / saleSubtotal) * 100 : undefined),
      subtotal: saleReceipt.subtotal ?? saleSubtotal,
      coupon_code: saleReceipt.coupon_code ?? appliedCoupon?.code ?? null,
    };
    if (isPayLaterSale && !/^PAY LATER\b/i.test(String(confirmed.note || ''))) {
      confirmed.note = payLaterHeadline(confirmed, formatCurrency);
    }
    if (confirmed.order_id && confirmed.tender_lines?.length > 1) {
      savePosTenderLines(confirmed.order_id, confirmed.tender_lines);
    }
    // Change is FE-local vs the cash slice (Pay Later) or the bill total (Cash).
    const payable = Number(saleReceipt.total);
    let changeFromServer = change;
    if (isPayLaterSale) {
      changeFromServer = 0;
    } else if (singleMethod === 'CASH' && Number.isFinite(payable) && cashAmount > 0) {
      changeFromServer = Math.max(0, cashAmount - payable);
    }
    resetCheckout();
    chargeAttemptRef.current = null;
    setPayLaterOpen(false);
    setPayLaterLotNote(false);
    setChangeDue(changeFromServer);
    setReceipt(confirmed);
    setLastReceipt(confirmed);
    if (rememberCountedSale(attempt.id)) {
      setSessionStats((s) => ({
        count: s.count + 1,
        total: s.total + (Number.isFinite(payable) ? payable : chargeDue),
      }));
    }
    reloadProducts();
    if (isPayLaterSale) {
      const due = confirmed.outstanding ?? confirmed.total;
      const paid = Number(confirmed.amount_paid) || 0;
      notify(paid > 0
        ? `Pay later ${confirmed.display_id || ''} · paid ${formatCurrency(paid)} · ${formatCurrency(due)} due`
        : `Pay later ${confirmed.display_id || ''} · ${formatCurrency(due)} due`);
    }
  };

  const handlePayLaterConfirm = async (customer) => {
    if (qtyIssue) {
      setChargeError(qtyIssue);
      return;
    }
    if (stockIssue) {
      setChargeError(stockIssue);
      return;
    }
    setCharging(true);
    setChargeError('');
    const queueOffline = async (saleCart, { attempted = false, error = null, attempt } = {}) => {
      const stamp = attempt || saleAttemptFor(saleCart, 'PAY_LATER');
      await finishQueuedPayLater({
        saleCart,
        customer,
        attempt: stamp,
        attempted,
        error,
      });
    };
    try {
      if (!browserOnline) {
        await queueOffline(cart);
        return;
      }
      const priorDue = cashDueForCart(cart);
      const raw = await reconcileCartLots(cart);
      if (raw.status === 'offline') {
        await queueOffline(raw.cart || cart);
        return;
      }
      const result = applyReconcileResult(raw, { priorDue, cashMethod: false });
      if (result.status === 'needs_pick') {
        setPayLaterOpen(false);
        setLotPick({
          item: result.pick.item,
          lots: result.pick.lots,
          cartLineKey: result.pick.cartLineKey,
          resume: 'pay_later',
          payLaterCustomer: customer,
        });
        return;
      }
      const attempt = saleAttemptFor(result.cart, 'PAY_LATER');
      try {
        await proceedCreatePosSale(result.cart, { payLaterCustomer: { ...customer, chargeAttempt: attempt } });
      } catch (err) {
        if (!err?.status || err?.code === 'sale_in_progress') {
          await queueOffline(result.cart, { attempted: true, error: err, attempt });
          return;
        }
        throw err;
      }
    } catch (err) {
      await refreshFailedItem(err);
      setChargeError(mapPosChargeError(err, { lines: cart }));
      throw err;
    } finally {
      setCharging(false);
    }
  };

  const handleCharge = async () => {
    if (!normalizedCounter) {
      setChargeError(POS_CHARGE_ERRORS.counter_required);
      return;
    }
    if (!counterIsValid) {
      setChargeError(POS_CHARGE_ERRORS.invalid_counter);
      return;
    }
    if (qtyIssue) {
      setChargeError(qtyIssue);
      return;
    }
    if (stockIssue) {
      setChargeError(stockIssue);
      return;
    }
    if (isPayLater) {
      await openPayLaterModal();
      return;
    }
    if (!browserOnline && isSplit) {
      setChargeError('Back online to split a bill.');
      return;
    }
    if (!canCharge) return;
    const queuePaid = async (saleCart, { attempted = false, error = null, attempt } = {}) => {
      const method = resolvePaymentMethod();
      const stamp = attempt || saleAttemptFor(saleCart, method);
      await finishQueuedCharge({
        saleCart,
        method,
        attempt: stamp,
        attempted,
        error,
      });
    };
    if (!browserOnline) {
      setCharging(true);
      setChargeError('');
      try {
        if (customerPhone && customerPhone.length !== 10) {
          setChargeError(POS_CHARGE_ERRORS.invalid_phone);
          return;
        }
        await queuePaid(cart);
      } finally {
        setCharging(false);
      }
      return;
    }
    setCharging(true);
    setChargeError('');
    try {
      if (customerPhone && customerPhone.length !== 10) {
        setChargeError(POS_CHARGE_ERRORS.invalid_phone);
        setCharging(false);
        return;
      }
      const priorDue = cashDueForCart(cart);
      const raw = await reconcileCartLots(cart);
      if (raw.status === 'offline') {
        await queuePaid(raw.cart || cart);
        return;
      }
      const result = applyReconcileResult(raw, { priorDue, cashMethod: singleMethod === 'CASH' });
      if (result.status === 'needs_pick') {
        setLotPick({
          item: result.pick.item,
          lots: result.pick.lots,
          cartLineKey: result.pick.cartLineKey,
          resume: 'charge',
        });
        setCharging(false);
        return;
      }
      // Reconciled prices may raise To pay — stop if cash tender is now short.
      if (singleMethod === 'CASH' && result.cashForCheck < result.nextDue) {
        setChargeError(
          `Short by ${formatCurrency(result.nextDue - result.cashForCheck)} after price update — adjust cash received.`,
        );
        setCharging(false);
        return;
      }
      const attempt = saleAttemptFor(result.cart, resolvePaymentMethod());
      try {
        await proceedCreatePosSale(result.cart, { attempt });
      } catch (err) {
        if (!err?.status || err?.code === 'sale_in_progress') {
          await queuePaid(result.cart, { attempted: true, error: err, attempt });
          return;
        }
        throw err;
      }
    } catch (err) {
      await refreshFailedItem(err);
      setChargeError(mapPosChargeError(err, { lines: cart }));
    } finally {
      setCharging(false);
    }
  };

  const customerPhoneForAmend = () => {
    const next = phoneDigits(customerPhone);
    const original = phoneDigits(editSale?.originalPhone);
    if (next === original) return undefined; // omit → keep
    if (!next) return ''; // clear
    return next;
  };

  const buildAmendPreviewBody = (lines = cart) => {
    const previewBody = {
      items: cartItemsPayloadFrom(lines),
      discount_percent: 0,
    };
    // Omit coupon_code when original had one — server keeps it.
    if (!editSale?.couponLocked && appliedCoupon?.code) {
      previewBody.coupon_code = appliedCoupon.code;
    }
    const phone = customerPhoneForAmend();
    if (phone !== undefined) previewBody.customer_phone = phone;
    return previewBody;
  };

  const runAmendPreview = async (lines = cart) => {
    const preview = await previewPosAmend(editSale.orderId, buildAmendPreviewBody(lines), user.idToken);
    if (!preview?.settlement?.direction) {
      throw Object.assign(new Error('Could not load settlement from preview.'), { code: 'invalid_preview' });
    }
    return preview;
  };

  const reloadEditSaleFromServer = async (reasonMessage) => {
    if (!editSale?.orderId) {
      exitEditMode();
      return;
    }
    try {
      const data = await getPosSale(editSale.orderId, user.idToken);
      beginEditSale(salePayloadFromGetResponse(data, editSale.orderId));
      notify(reasonMessage
        ? `${reasonMessage} Bill reloaded — preview again.`
        : 'Bill reloaded — preview again.');
    } catch (reloadErr) {
      notify(mapPosAmendError(reloadErr));
      exitEditMode();
    }
  };

  const proceedAmendPreview = async (lines) => {
    const preview = await runAmendPreview(lines);
    setAmendPreview(preview);
  };

  const handlePreviewAmend = async () => {
    if (!canPreviewAmend) {
      if (!cart.length) {
        const msg = POS_CHARGE_ERRORS.items_required;
        setChargeError(msg);
        notify(msg);
      }
      return;
    }
    if (customerPhone && customerPhone.length !== 10) {
      setChargeError(POS_CHARGE_ERRORS.invalid_phone);
      notify(POS_CHARGE_ERRORS.invalid_phone);
      return;
    }
    const minErr = minOrderChargeError(cart);
    if (minErr) {
      setChargeError(minErr.message);
      notify(minErr.message);
      return;
    }
    setCharging(true);
    setChargeError('');
    setAmendError('');
    try {
      const priorDue = cashDueForCart(cart);
      const raw = await reconcileCartLots(cart);
      const result = applyReconcileResult(raw, { priorDue, cashMethod: false });
      if (result.status === 'needs_pick') {
        setLotPick({
          item: result.pick.item,
          lots: result.pick.lots,
          cartLineKey: result.pick.cartLineKey,
          resume: 'amend',
        });
        return;
      }
      await proceedAmendPreview(result.cart);
    } catch (err) {
      const code = err?.code || err?.message;
      const message = mapPosAmendError(err, { lines: cart });
      await refreshFailedItem(err);
      setChargeError(message);
      setAmendPreview(null);
      notify(message);
      if (code === 'amend_day_locked' || code === 'not_found') {
        exitEditMode();
      } else if (code === 'amend_conflict') {
        await reloadEditSaleFromServer(message);
      } else if (code === 'item_unavailable' || code === 'lot_mismatch') {
        // Catalog item/lot may be inactive or gone — stay in edit so cashier can remove the line.
        setChargeError(message);
      }
    } finally {
      setCharging(false);
    }
  };

  const handleConfirmAmend = async ({ payment_method } = {}) => {
    if (!editSale?.orderId || !amendPreview || amending || repreviewing) return;

    const settlement = amendPreview.settlement;
    const settlement_direction = String(settlement?.direction || '').toUpperCase();
    // EVEN must send amount 0 (required) — do not omit settlement_amount.
    const settlement_amount = settlement_direction === 'EVEN'
      ? (settlement?.amount ?? 0)
      : settlement?.amount;
    if (!settlement_direction || settlement_amount == null || !Number.isFinite(Number(settlement_amount))) {
      const msg = 'Preview incomplete — run Preview amend again.';
      setAmendError(msg);
      notify(msg);
      return;
    }
    const amendingPayLater = String(editSale?.originalPaymentMethod || selectedMethods[0] || '')
      .toUpperCase() === 'PAY_LATER';
    if ((settlement_direction === 'DUE' || settlement_direction === 'REFUND' || settlement_direction === 'EVEN')
      && !payment_method
      && !amendingPayLater) {
      setAmendError(POS_CHARGE_ERRORS.invalid_payment_method);
      notify(POS_CHARGE_ERRORS.invalid_payment_method);
      return;
    }

    const minErr = minOrderChargeError(cart);
    if (minErr) {
      setAmendError(minErr.message);
      notify(minErr.message);
      return;
    }

    setAmending(true);
    setAmendError('');
    try {
      const amendBody = {
        items: cartItemsPayload(),
        settlement_direction,
        settlement_amount: Number(settlement_amount),
        discount_percent: 0,
      };
      if (payment_method) amendBody.payment_method = payment_method;
      const phone = customerPhoneForAmend();
      if (phone !== undefined) amendBody.customer_phone = phone;

      const data = await amendPosSale(editSale.orderId, amendBody, user.idToken);
      const saleReceipt = data?.receipt || null;
      const sale = data?.sale || null;
      const confirmed = buildConfirmedAmendReceipt(sale, saleReceipt, {
        order_id: editSale.orderId,
        display_id: editSale.displayId,
      });
      confirmed.counter = confirmed.counter || POS_COUNTER;
      confirmed.gstin = confirmed.gstin || POS_GSTIN;
      confirmed.cashier_name = confirmed.cashier_name || user.name;
      if (confirmed.customer_number == null || confirmed.customer_number === '') {
        delete confirmed.customer_number;
      }
      if (settlement_direction === 'REFUND' && !amendingPayLater) {
        const returned = Number(settlement_amount);
        if (returned > 0) confirmed.amount_returned = returned;
      }

      setAmendPreview(null);
      resetCheckout();
      setHistoryOpen(false);
      setChangeDue(0);
      setReceipt(confirmed);
      setLastReceipt(confirmed);
      await reloadProducts();
      notify(`Bill ${confirmed.display_id || editSale.displayId} revised (r${confirmed.receipt_revision || 2}).`);
    } catch (err) {
      const code = err?.code || err?.message;
      const message = mapPosAmendError(err, { lines: cart });
      await refreshFailedItem(err);
      notify(message);

      if (code === 'amount_mismatch') {
        setAmending(false);
        setRepreviewing(true);
        setAmendError(message);
        try {
          const preview = await runAmendPreview();
          setAmendPreview(preview);
          setAmendError('Totals changed — preview refreshed. Confirm again with the new settlement.');
        } catch (previewErr) {
          const previewMessage = mapPosAmendError(previewErr, { lines: cart });
          setAmendPreview(null);
          setChargeError(previewMessage);
          notify(previewMessage);
        } finally {
          setRepreviewing(false);
        }
        return;
      }

      setAmendError(message);
      setChargeError(message);

      if (code === 'amend_conflict') {
        setAmendPreview(null);
        setAmending(false);
        await reloadEditSaleFromServer(message);
        return;
      }
      if (code === 'amend_day_locked' || code === 'not_found') {
        setAmendPreview(null);
        exitEditMode();
        return;
      }
      if (code === 'coupon_locked') {
        // Stay on settlement / edit so cashier can continue without swapping coupon.
        return;
      }
      if (code === 'item_unavailable' || code === 'lot_mismatch' || code === 'insufficient_stock') {
        // Catalog may have changed; close settlement and let cashier fix the cart.
        setAmendPreview(null);
      }
    } finally {
      setAmending(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Point of Sale"
        title={isEditMode ? `Edit bill ${editSale.displayId}` : 'New Sale'}
        subtitle={isEditMode
          ? 'Change items, then preview settlement. Stock and totals come from the server.'
          : cart.some((l) => l.soldBy === SOLD_BY.WEIGHT)
            ? 'Repeat scan adds weight.'
            : 'Scan or search packed goods; tap loose items to weigh.'}
        actions={(
          <div className={styles.headerActions}>
            <div className={styles.headerTopRow}>
              {!isEditMode && (
                <label className={styles.counterField}>
                  <span>Counter number</span>
                  <div className={styles.counterInputWrap}>
                    <input
                      type="text"
                      value={counterNumber}
                      maxLength={16}
                      pattern="[A-Za-z0-9]{1,16}"
                      autoComplete="off"
                      aria-invalid={Boolean(counterNumber) && !counterIsValid}
                      aria-describedby="pos-counter-hint"
                      onChange={(e) => {
                        setCounterNumber(e.target.value.toUpperCase());
                        setCounterSetupError('');
                        if (
                          chargeError === POS_CHARGE_ERRORS.counter_required
                          || chargeError === POS_CHARGE_ERRORS.invalid_counter
                        ) setChargeError('');
                      }}
                    />
                    <small
                      id="pos-counter-hint"
                      className={counterNumber && !counterIsValid ? styles.counterHintError : ''}
                    >
                      1–16 letters or numbers
                    </small>
                  </div>
                </label>
              )}
              {isEditMode ? (
                <Button type="button" variant="outline" size="sm" onClick={exitEditMode}>
                  {historyOpen ? 'Back' : 'Cancel edit'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={History}
                  onClick={() => setHistoryOpen(true)}
                >
                  History
                </Button>
              )}
            </div>
            {shownSaleCount > 0 && (
              <div className={styles.sessionActions}>
                <div className={styles.sessionStatRow}>
                  {!salesHidden && (
                    <div className={styles.sessionStat}>
                      <span>{shownSaleCount} sale{shownSaleCount > 1 ? 's' : ''} today</span>
                      <strong>{formatCurrency(shownSaleTotal)}</strong>
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.salesHideBtn}
                    aria-pressed={salesHidden}
                    aria-label={salesHidden ? 'Show today\'s sales' : 'Hide today\'s sales'}
                    title={salesHidden ? 'Show sales' : 'Hide sales'}
                    onClick={() => {
                      setSalesHidden((hidden) => {
                        const next = !hidden;
                        try {
                          localStorage.setItem('bhima-store.pos-sales-hidden', next ? '1' : '0');
                        } catch {
                          // The toggle still applies on this screen.
                        }
                        return next;
                      });
                    }}
                  >
                    {salesHidden ? <Eye size={15} strokeWidth={2.25} /> : <EyeOff size={15} strokeWidth={2.25} />}
                  </button>
                </div>
                {lastReceipt && (
                  <button type="button" className={styles.reprintLastBtn} onClick={() => setReprintOpen(true)}>
                    Reprint last sale
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      />

      <div className={styles.grid}>
        <div className={styles.left}>
          <div className={styles.scanHeader}>
            <p className={styles.scanLabel}>Scan field — most scanners type directly into it</p>
            <div className={styles.scanHeaderActions}>
              {browserOnline && (
                <>
                  <Button type="button" variant="outline" size="sm" icon={Plus} onClick={() => openInstantOnboard('EACH')}>
                    Add packaged
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => openInstantOnboard('WEIGHT')}>
                    Loose / weigh
                  </Button>
                </>
              )}
              <button
                type="button"
                className={`${styles.capturePill} ${captureFocused ? styles.capturingOn : styles.capturePaused}`}
                onClick={focusScanField}
              >
                <span className={styles.captureDot} />
                {captureFocused ? 'Listening for input' : 'Paused — click to resume'}
              </button>
            </div>
          </div>
          <div className={styles.scanBox}>
            <ScanBarcode size={20} className={styles.scanIcon} />
            <input
              ref={scanRef}
              autoFocus
              value={scanValue}
              onChange={(e) => { setScanValue(e.target.value); setScanError(''); }}
              onKeyDown={handleScanKeyDown}
              onFocus={() => {
                setCaptureFocused(true);
                if (scanRefocusTimerRef.current) {
                  clearTimeout(scanRefocusTimerRef.current);
                  scanRefocusTimerRef.current = null;
                }
              }}
              onBlur={(e) => {
                setCaptureFocused(false);
                scheduleScanRefocus(e.relatedTarget);
              }}
              placeholder="Scan barcode, or type a product name / SKU…"
              className={styles.scanInput}
              aria-label="Barcode scanner input"
            />
            {scanValue && (
              <button
                className={styles.clearScan}
                onClick={() => { setScanValue(''); setScanError(''); focusScanField(); }}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
          {scanError && <p className={styles.scanError}><AlertTriangle size={13} /> {scanError}</p>}

          <p className={styles.sectionLabel}>{query ? `Results for "${scanValue.trim()}"` : 'Quick add'}</p>
          <div className={styles.productGrid}>
            {(query ? searchResults : activeProducts).length === 0 ? (
              <EmptyState icon={Search} title="No matches" description="Try a different name, SKU, or barcode." />
            ) : (query ? searchResults : activeProducts).map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                chargePrice={posDisplayPrice(p, cart)}
                inCartQty={tileCartQty(p.id)}
                onAdd={() => handleQuickAdd(p, { keepValue: !!query })}
              />
            ))}
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.cartPanel}>
            <div className={styles.cartHeader}>
              <h2><ShoppingCart size={16} /> Current sale</h2>
              {(cart.length > 0 || undoStack.length > 0 || redoStack.length > 0) && (
                <div className={styles.cartHeaderActions}>
                  <button
                    type="button"
                    className={styles.undoLast}
                    onClick={undoLastAdd}
                    disabled={!undoStack.length}
                    title="Undo last quantity change"
                  >
                    <Undo2 size={13} /> Undo
                  </button>
                  <button
                    type="button"
                    className={styles.undoLast}
                    onClick={redoLastUndo}
                    disabled={!redoStack.length}
                    title="Redo last quantity change"
                  >
                    <Redo2 size={13} /> Redo
                  </button>
                  {cart.length > 0 && (
                    <button type="button" className={styles.clearAll} onClick={clearCart}>Clear all</button>
                  )}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className={styles.cartSearchBox}>
                <Search size={13} />
                <input
                  placeholder="Search items in this sale…"
                  value={cartSearch}
                  onChange={(e) => setCartSearch(e.target.value)}
                />
                {cartSearch && (
                  <button onClick={() => setCartSearch('')} aria-label="Clear search">
                    <X size={13} />
                  </button>
                )}
              </div>
            )}

            <div className={styles.cartList}>
              {cart.length === 0 ? (
                <EmptyState icon={ShoppingCart} title="Cart is empty" description="Scan or tap a product to begin." />
              ) : visibleCartLines.length === 0 ? (
                <p className={styles.cartNoMatch}>No items match "{cartSearch.trim()}"</p>
              ) : visibleCartLines.map((line) => {
                const serial = cart.findIndex((l) => l.lineKey === line.lineKey) + 1;
                const qtyProblem = orderQtyProblem(line.qty, line);
                const underPackMin = line.soldBy !== SOLD_BY.WEIGHT && line.qty < minimumPackQty(line);
                return (
                <div
                  key={line.lineKey}
                  className={`${styles.cartLine} ${flashId === line.lineKey ? styles.flash : ''} ${(underPackMin || qtyProblem) ? styles.lineBelowMin : ''}`}
                >
                  <span className={styles.lineSerial}>{serial}</span>
                  <span className={styles.lineEmoji} style={{ background: `${line.swatch}22`, color: line.swatch }}>
                    {line.emoji}
                  </span>
                  <div className={styles.lineInfo}>
                    <p className={styles.lineName} title={line.name}>
                      {line.name}
                      {line.catalogComplete === false && <span className={styles.newItemBadge}>New · Needs details</span>}
                    </p>
                    <p className={styles.linePrice}>
                      {formatPrice(line, formatCurrency)}
                      {line.lotId ? ' · lot' : ''}
                      {underPackMin && (
                        <span className={styles.lineMinQtyWarn}>
                          {' · '}Minimum {minimumPackQty(line)}
                        </span>
                      )}
                      {(() => {
                        const s = getItemSavings(line, line.mrp);
                        if (s.unitSavings <= 0) return null;
                        const sText = formatItemSavingsText(s, line, formatCurrency);
                        return sText ? (
                          <span className={styles.lineSavings}>
                            {' · '}{sText}
                          </span>
                        ) : null;
                      })()}
                    </p>
                    {qtyProblem && <p className={styles.lineQtyError}>{qtyProblem}</p>}
                  </div>
                  {line.soldBy === SOLD_BY.WEIGHT ? (
                    <button
                      type="button"
                      className={styles.lineWeight}
                      onClick={() => editWeightLine(line)}
                      aria-label={`Edit weight for ${line.name}`}
                    >
                      {formatQty(line.qty, line)}
                    </button>
                  ) : (
                    <div className={styles.lineQty}>
                      <button onClick={() => changeQty(line.lineKey, -1)} aria-label="Decrease quantity">
                        <Minus size={12} />
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={styles.lineQtyInput}
                        value={qtyDrafts[line.lineKey] !== undefined ? qtyDrafts[line.lineKey] : String(line.qty)}
                        aria-label={`Quantity for ${line.name}`}
                        onFocus={(e) => {
                          setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: String(line.qty) }));
                          e.target.select();
                        }}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^\d]/g, '');
                          if (digits === '') {
                            setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: '' }));
                            return;
                          }
                          const parsed = Math.floor(Number(digits));
                          const stockCredit = isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0;
                          const maxQty = line.catalogComplete !== false
                            ? line.stock + stockCredit
                            : Number.POSITIVE_INFINITY;
                          if (Number.isFinite(maxQty) && parsed > maxQty) {
                            // Do not allow typing above available stock
                            const capped = Math.max(0, Math.floor(maxQty));
                            setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: String(capped) }));
                            if (capped > 0) setQty(line.lineKey, capped);
                            return;
                          }
                          setQtyDrafts((prev) => ({ ...prev, [line.lineKey]: digits }));
                          setQty(line.lineKey, digits);
                        }}
                        onBlur={() => {
                          setQtyDrafts((prev) => {
                            const next = { ...prev };
                            delete next[line.lineKey];
                            return next;
                          });
                          const draft = qtyDrafts[line.lineKey];
                          if (draft === undefined || draft === '' || Number(draft) <= 0) {
                            return;
                          }
                          const stockCredit = isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0;
                          const maxQty = line.catalogComplete !== false
                            ? line.stock + stockCredit
                            : Number.POSITIVE_INFINITY;
                          const parsed = Math.floor(Number(draft));
                          if (Number.isFinite(maxQty) && parsed > maxQty) {
                            if (maxQty > 0) setQty(line.lineKey, maxQty);
                            return;
                          }
                          setQty(line.lineKey, draft);
                        }}
                      />
                      <button
                        onClick={() => changeQty(line.lineKey, 1)}
                        disabled={
                          line.catalogComplete !== false
                          && line.soldBy !== SOLD_BY.WEIGHT
                          && line.qty >= line.stock + (isEditMode ? (Number(amendStockCredit[line.lineKey]) || 0) : 0)
                        }
                        aria-label="Increase quantity"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                  <span className={styles.lineTotal}>{formatCurrency(lineAmount(line.price, line.qty))}</span>
                  <button className={styles.lineRemove} onClick={() => removeLine(line.lineKey)} aria-label="Remove item">
                    <Trash2 size={13} />
                  </button>
                </div>
                );
              })}
            </div>

            {cart.length > 0 && (
              <div className={styles.cartCheckout}>
                <div className={styles.customerPhoneBlock}>
                  <label className={styles.customerPhoneField}>
                    <span className={styles.sectionLabel}><Phone size={13} /> Customer phone</span>
                    <div className={styles.customerPhoneInput}>
                      <span>+91</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="10-digit mobile (optional)"
                        value={customerPhone}
                        maxLength={10}
                        onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        aria-label="Customer phone number"
                      />
                    </div>
                  </label>
                </div>

                <div className={styles.discountBlock}>
                  {appliedCoupon ? (
                    <div className={styles.couponApplied}>
                      <span>
                        <Ticket size={13} /> {appliedCoupon.code} applied
                        {editSale?.couponLocked ? ' (locked)' : ''}
                      </span>
                      {!editSale?.couponLocked && (
                        <button onClick={removeCoupon} aria-label="Remove coupon"><X size={13} /></button>
                      )}
                    </div>
                  ) : (
                    <div className={styles.couponRow}>
                      <input
                        placeholder="Coupon code"
                        value={couponInput}
                        disabled={!browserOnline || couponChecking || Boolean(editSale?.couponLocked)}
                        onChange={(e) => { setCouponInput(e.target.value); setCouponError(''); }}
                        onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                        style={{ textTransform: 'uppercase' }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={applyCoupon}
                        disabled={!browserOnline || !couponInput.trim() || couponChecking || Boolean(editSale?.couponLocked)}
                      >
                        {couponChecking ? 'Checking…' : 'Apply'}
                      </Button>
                    </div>
                  )}
                  {couponError && <p className={styles.couponErrorText}>{couponError}</p>}
                </div>

                <div className={styles.paymentBlock}>
                  <div className={styles.paymentHeadRow}>
                    <p className={styles.sectionLabel}>Payment method</p>
                    {!isEditMode && isPayLater && (
                      <span className={styles.splitToggleHint}>
                        Collect customer details on charge
                      </span>
                    )}
                    {!isEditMode && !isPayLater && selectedMethods.length > 0 && (
                      <span className={styles.splitToggleHint}>
                        {isSplit ? 'Split payment' : 'Tap another method to split'}
                      </span>
                    )}
                    {isEditMode && (
                      <span className={styles.splitToggleHint}>
                        {editSale?.originalPaymentMethod
                          && selectedMethods[0]
                          && selectedMethods[0] !== editSale.originalPaymentMethod
                          ? `Was ${editSale.originalPaymentMethod} — updating tender`
                          : 'Change if the customer switched how they pay'}
                      </span>
                    )}
                  </div>
                  <div className={styles.paymentRow}>
                    {(isEditMode
                      ? (editSale?.originalPaymentMethod === 'PAY_LATER'
                        ? PAYMENT_METHODS.filter((m) => m.key === 'PAY_LATER')
                        : EDIT_PAYMENT_METHODS)
                      : PAYMENT_METHODS
                    ).map(({ key, label, icon: Icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.paymentBtn} ${selectedMethods.includes(key) ? styles.paymentActive : ''}`}
                        onClick={() => togglePaymentMethod(key)}
                        disabled={isEditMode && editSale?.originalPaymentMethod === 'PAY_LATER'}
                      >
                        <Icon size={16} /> {label}
                      </button>
                    ))}
                  </div>

                  {singleMethod === 'CASH' && (
                    <div className={styles.tenderedRow}>
                      {isEditMode && <span className={styles.sectionLabel}>Amount given</span>}
                      <div className={styles.tenderedInput}>
                        <span>₹</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder={isEditMode ? 'Amount given' : 'Amount received'}
                          aria-label={isEditMode ? 'Amount given' : 'Cash received'}
                          value={paymentAmounts.CASH || ''}
                          onChange={(e) => setPaymentAmount('CASH', e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                          }}
                        />
                      </div>
                      {paymentAmounts.CASH !== undefined && paymentAmounts.CASH !== '' && (
                        <span className={cashAmount >= cashTarget ? styles.changeOk : styles.changeShort}>
                          {cashAmount >= cashTarget
                            ? `${isEditMode ? 'Change given' : 'Change due'} ${formatCurrency(computedChangeDue)}`
                            : `Short by ${formatCurrency(cashTarget - cashAmount)}`}
                        </span>
                      )}
                      {isEditMode && editCashPayable != null && editCashPayable !== total && (
                        <p className={styles.gstNote}>
                          Cash collects {formatCurrency(editCashPayable)}.
                        </p>
                      )}
                    </div>
                  )}

                  {!isEditMode && isSplit && (
                    <div className={styles.splitPayments}>
                      {selectedMethods.map((m) => {
                        const meta = PAYMENT_METHODS.find((pm) => pm.key === m);
                        return (
                          <div key={m} className={styles.splitRow}>
                            <span className={styles.splitLabel}><meta.icon size={13} /> {meta.label}</span>
                            <div className={styles.splitAmountInput}>
                              <span>₹</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                placeholder="0"
                                aria-label={`${meta.label} amount`}
                                value={paymentAmounts[m] || ''}
                                onChange={(e) => setPaymentAmount(m, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                                }}
                              />
                            </div>
                            <button type="button" className={styles.fillRemainingBtn} onClick={() => fillRemainingAmount(m)}>
                              Fill
                            </button>
                          </div>
                        );
                      })}
                      <div className={styles.splitSummary}>
                        <span>Collected {formatCurrency(paymentTotal)} of {formatCurrency(total)}</span>
                        {balanceDue > 0 && <span className={styles.changeShort}>Short by {formatCurrency(balanceDue)}</span>}
                        {balanceDue === 0 && computedChangeDue > 0 && (
                          <span className={styles.changeOk}>Change due {formatCurrency(computedChangeDue)}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.totals}>
                  <div className={styles.billRow}><span>Items</span><span>{cartQtyFooter(cart)}</span></div>
                  <div className={styles.billRow}><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                  {discount > 0 && (
                    <div className={`${styles.billRow} ${styles.billDiscount}`}>
                      <span>
                        {formatDiscountLabel({
                          coupon_code: appliedCoupon?.code,
                          discount_amount: discount,
                          subtotal,
                          discount_percent: appliedCoupon?.discountPercent,
                        })}
                      </span>
                      <span>−{formatCurrency(discount)}</span>
                    </div>
                  )}
                  {!isEditMode && previewRoundOff !== 0 && (
                    <div className={styles.billRow}>
                      <span>Round off</span>
                      <span>
                        {previewRoundOff > 0 ? '+' : ''}
                        {formatCurrency(previewRoundOff)}
                      </span>
                    </div>
                  )}
                  <div className={`${styles.billRow} ${styles.billTotal}`}>
                    <span>{isEditMode ? 'Cart total (est.)' : 'To pay'}</span>
                    <span>{formatCurrency(isEditMode ? total : chargeDue)}</span>
                  </div>
                  {(() => {
                    const cartSavings = calculateBillSavings({ line_items: cart, discount_amount: discount, subtotal }, products);
                    if (cartSavings.totalSavings <= 0) return null;
                    return (
                      <div className={`${styles.billRow} ${styles.billSavings}`}>
                        <span>Total Savings</span>
                        <span>
                          {formatCurrency(cartSavings.totalSavings)}
                          {cartSavings.totalSavingsPct > 0 ? ` (${formatDiscountPercent(cartSavings.totalSavingsPct)})` : ''}
                        </span>
                      </div>
                    );
                  })()}
                  {isEditMode && editSale?.originalTotal != null && (
                    <div className={styles.billRow}>
                      <span>Original bill</span>
                      <span>{formatCurrency(editSale.originalTotal)}</span>
                    </div>
                  )}
                  {isEditMode && editSale?.originalPaymentMethod === 'PAY_LATER' && editSale.originalAmountPaid > 0 && (
                    <div className={styles.billRow}>
                      <span>Already collected</span>
                      <span>{formatCurrency(editSale.originalAmountPaid)}</span>
                    </div>
                  )}
                  {priceUnconfirmed && (
                    <p className={styles.gstNote}>Price and stock confirm when this bill is sent.</p>
                  )}
                  {!browserOnline && !isEditMode && cart.some((line) => line.requiresLot && !line.lotId) && (
                    <p className={styles.gstNote}>A lot will be picked when this bill syncs.</p>
                  )}
                  <p className={styles.gstNote}>
                    {isEditMode
                      ? 'Preview to get Collect / Refund / Even from the server before confirming.'
                      : isPayLater
                        ? (browserOnline
                          ? 'Pay Later keeps this bill open. Collect part now on the next screen, or the whole amount later.'
                          : 'Pay Later queues the whole bill. Collect Cash, UPI, or Card from Pay Laters.')
                        : singleMethod === 'CASH' && previewRoundOff !== 0
                          ? 'Cash rounds to the nearest rupee. Final To pay is confirmed on charge.'
                          : 'Prices are inclusive of GST (totals confirmed on charge)'}
                  </p>
                </div>

                {paidWaiting.length > 0 && (
                  <div className={styles.queuedBills}>
                    {paidWaiting.map((row) => {
                      const methodLabel = PAYMENT_METHODS.find((method) => method.key === row.paymentMethod)?.label
                        || row.paymentMethod;
                      return (
                        <div key={row.clientSaleId} className={styles.queuedBill}>
                          <p>
                            {methodLabel} · {localReference(row.clientSaleId)} · {formatCurrency(queuedBillAmount(row))}
                            {' · Waiting to sync'}
                          </p>
                          {row.errorMessage && <p className={styles.queuedBillError}>{row.errorMessage}</p>}
                          {row.variance ? (
                            <p className={styles.queuedBillError}>
                              Collected {formatCurrency(row.collectedAmount)} · server {formatCurrency(row.serverTotal)}
                            </p>
                          ) : null}
                          {row.errorMessage && canEditCharge(row) && (row.items || []).map((line, index) => (
                            <label key={`${line.item_id}-${index}`} className={styles.queuedLot}>
                              <span>{line.name || line.item_id}</span>
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                aria-label={`Quantity for ${line.name || line.item_id}`}
                                defaultValue={line.qty}
                                onBlur={(e) => {
                                  const qty = Number(e.target.value);
                                  if (!Number.isFinite(qty) || qty < 0) return;
                                  const items = (row.items || [])
                                    .map((entry, entryIndex) => (
                                      entryIndex === index ? { ...entry, qty } : entry
                                    ))
                                    .filter((entry) => Number(entry.qty) > 0);
                                  posOfflineQueue.updateCharge(row.clientSaleId, { items });
                                }}
                              />
                            </label>
                          ))}
                          <div className={styles.queuedBillActions}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const slip = buildProvisionalReceipt(row, { cashierName: user?.name });
                                setChangeDue(0);
                                setReceipt(slip);
                                setLastReceipt(slip);
                              }}
                            >
                              Reprint slip
                            </Button>
                            {canDiscardCharge(row) && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  const removed = await posOfflineQueue.discardCharge(row.clientSaleId);
                                  notify(removed ? 'Unsynced bill discarded.' : 'This bill cannot be discarded.');
                                }}
                              >
                                Discard
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {chargeError && <p className={styles.scanError}><AlertTriangle size={13} /> {chargeError}</p>}
                {isEditMode && !cart.length && (
                  <p className={styles.scanError}>
                    <AlertTriangle size={13} /> Empty cart cannot be saved — void the bill from History instead.
                  </p>
                )}

                {isEditMode ? (
                  <Button
                    size="lg"
                    className={styles.chargeBtn}
                    disabled={!canPreviewAmend}
                    onClick={handlePreviewAmend}
                  >
                    {charging
                      ? 'Saving…'
                      : !cart.length
                        ? 'Add items to save'
                        : selectedMethods.length !== 1
                          ? 'Select payment method'
                          : 'Save changes'}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className={styles.chargeBtn}
                    disabled={
                      Boolean(qtyIssue || stockIssue)
                      || (isPayLater
                        ? (charging || payLaterOpen || !cart.length)
                        : (!canCharge || (!browserOnline && isSplit)))
                    }
                    onClick={handleCharge}
                  >
                    {charging
                      ? (isPayLater ? 'Saving…' : 'Charging…')
                      : qtyIssue
                        ? qtyIssue
                      : stockIssue
                        ? stockIssue
                      : !normalizedCounter
                        ? 'Enter counter number'
                        : !counterIsValid
                          ? 'Enter a valid counter number'
                      : !browserOnline && isSplit
                        ? 'Back online to split a bill'
                      : isPayLater
                        ? `Pay Later ${formatCurrency(isPayLater ? total : chargeDue)}`
                        : selectedMethods.length > 0
                          ? `Charge ${formatCurrency(chargeDue)}`
                          : 'Select a payment method'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {browserOnline && instantOnboard && (
        <InstantOnboardModal
          mode={instantOnboard.mode}
          initialBarcode={instantOnboard.barcode}
          token={user.idToken}
          products={activeProducts}
          onClose={() => setInstantOnboard(null)}
          onCreate={handleInstantOnboard}
          onUseExisting={addExistingInstantProduct}
        />
      )}

      {payLaterOpen && (
        <PayLaterCustomerModal
          amount={total}
          initialPhone={customerPhone}
          token={user.idToken}
          submitting={charging}
          offline={!browserOnline}
          lotNote={payLaterLotNote}
          onClose={() => {
            if (charging) return;
            setPayLaterOpen(false);
            setPayLaterLotNote(false);
          }}
          onConfirm={handlePayLaterConfirm}
        />
      )}

      {counterSetupOpen && (
        <div className={styles.counterSetupOverlay} role="dialog" aria-modal="true" aria-labelledby="counter-setup-title">
          <form className={styles.counterSetupModal} onSubmit={confirmCounterSetup}>
            <h2 id="counter-setup-title">Enter counter number</h2>
            <p>Set the counter for this POS session. You can change it later from the POS header.</p>
            <label className={styles.counterSetupField}>
              <span>Counter number</span>
              <input
                type="text"
                value={counterNumber}
                maxLength={16}
                pattern="[A-Za-z0-9]{1,16}"
                autoComplete="off"
                autoFocus
                aria-invalid={Boolean(counterSetupError)}
                onChange={(e) => {
                  setCounterNumber(e.target.value.toUpperCase());
                  setCounterSetupError('');
                }}
              />
              <small>1–16 letters or numbers</small>
            </label>
            {counterSetupError && (
              <p className={styles.counterSetupError} role="alert">
                <AlertTriangle size={14} /> {counterSetupError}
              </p>
            )}
            <Button type="submit">Continue to POS</Button>
          </form>
        </div>
      )}

      {lotPick && (
        <LotPickerModal
          item={lotPick.item}
          lots={lotPick.lots}
          onClose={() => { setLotPick(null); focusScanField(); }}
          onSelect={(lot) => {
            const item = lotPick.item;
            const existingLineKey = lotPick.cartLineKey;
            const resume = lotPick.resume;
            const pendingPayLaterCustomer = lotPick.payLaterCustomer;
            const instantQty = lotPick.instantEntry ? lotPick.qty : null;
            setLotPick(null);

            // Existing cart line missing a lot — update in place; keep qty.
            if (existingLineKey) {
              let nextCart = null;
              const remaps = [];
              setCart((prev) => {
                nextCart = prev.map((line) => {
                  if (line.lineKey !== existingLineKey) return line;
                  const { line: updated, oldKey, newKey } = applyLotFieldsToLine(line, lot, { choice: 'user' });
                  if (oldKey !== newKey) remaps.push({ oldKey, newKey });
                  upsertLotPricedCatalog(updated, lot);
                  return updated;
                });
                return nextCart;
              });
              applyQtyDraftRemaps(remaps);
              if (resume === 'charge') {
                setCharging(true);
                setChargeError('');
                (async () => {
                  try {
                    const source = nextCart || cart;
                    if (!browserOnline) {
                      await finishQueuedCharge({
                        saleCart: source,
                        method: singleMethod,
                        attempt: saleAttemptFor(source, singleMethod),
                      });
                      return;
                    }
                    const priorDue = cashDueForCart(source);
                    const raw = await reconcileCartLots(source);
                    if (raw.status === 'offline') {
                      await finishQueuedCharge({
                        saleCart: raw.cart || source,
                        method: singleMethod,
                        attempt: saleAttemptFor(raw.cart || source, singleMethod),
                      });
                      return;
                    }
                    const result = applyReconcileResult(raw, {
                      priorDue,
                      cashMethod: singleMethod === 'CASH',
                    });
                    if (result.status === 'needs_pick') {
                      setLotPick({
                        item: result.pick.item,
                        lots: result.pick.lots,
                        cartLineKey: result.pick.cartLineKey,
                        resume: 'charge',
                      });
                      return;
                    }
                    if (singleMethod === 'CASH' && result.cashForCheck < result.nextDue) {
                      setChargeError(
                        `Short by ${formatCurrency(result.nextDue - result.cashForCheck)} after price update — adjust cash received.`,
                      );
                      return;
                    }
                    const attempt = saleAttemptFor(result.cart, resolvePaymentMethod());
                    try {
                      await proceedCreatePosSale(result.cart, { attempt });
                    } catch (err) {
                      if (!err?.status || err?.code === 'sale_in_progress') {
                        await finishQueuedCharge({
                          saleCart: result.cart,
                          method: resolvePaymentMethod(),
                          attempt,
                          attempted: true,
                          error: err,
                        });
                        return;
                      }
                      throw err;
                    }
                  } catch (err) {
                    await refreshFailedItem(err);
                    setChargeError(mapPosChargeError(err, { lines: cart }));
                  } finally {
                    setCharging(false);
                  }
                })();
                return;
              }
              if (resume === 'amend') {
                setCharging(true);
                setChargeError('');
                setAmendError('');
                (async () => {
                  try {
                    const raw = await reconcileCartLots(nextCart || cart);
                    const result = applyReconcileResult(raw, { cashMethod: false });
                    if (result.status === 'needs_pick') {
                      setLotPick({
                        item: result.pick.item,
                        lots: result.pick.lots,
                        cartLineKey: result.pick.cartLineKey,
                        resume: 'amend',
                      });
                      return;
                    }
                    await proceedAmendPreview(result.cart);
                  } catch (err) {
                    const message = mapPosAmendError(err, { lines: cart });
                    await refreshFailedItem(err);
                    setChargeError(message);
                    notify(message);
                  } finally {
                    setCharging(false);
                  }
                })();
                return;
              }
              if (resume === 'pay_later') {
                if (pendingPayLaterCustomer) {
                  setCharging(true);
                  setChargeError('');
                  (async () => {
                    try {
                      const raw = await reconcileCartLots(nextCart || cart);
                      const result = applyReconcileResult(raw, { cashMethod: false });
                      if (result.status === 'needs_pick') {
                        setLotPick({
                          item: result.pick.item,
                          lots: result.pick.lots,
                          cartLineKey: result.pick.cartLineKey,
                          resume: 'pay_later',
                          payLaterCustomer: pendingPayLaterCustomer,
                        });
                        return;
                      }
                      await proceedCreatePosSale(result.cart, { payLaterCustomer: pendingPayLaterCustomer });
                    } catch (err) {
                      await refreshFailedItem(err);
                      setChargeError(mapPosChargeError(err, { lines: cart }));
                    } finally {
                      setCharging(false);
                    }
                  })();
                } else {
                  setPayLaterOpen(true);
                }
              }
              return;
            }

            if (instantQty != null) {
              addToCart(item, {
                keepValue: true,
                lotId: lot.lotId,
                unitPrice: lot.sellingPrice,
                lotQty: lot.qty,
                mrp: lot.mrp,
                requiresLot: true,
                lotChoice: 'user',
                qty: instantQty,
                soldBy: item.soldBy,
              });
              return;
            }
            if (isSoldByWeight(item)) {
              openWeighSheet({
                item,
                unitPrice: lot.sellingPrice,
                lotQty: lot.qty,
                mrp: lot.mrp,
                requiresLot: true,
                lotId: lot.lotId,
                lotChoice: 'user',
                keepValue: true,
                mode: 'add',
              });
              return;
            }
            addToCart(item, {
              keepValue: true,
              lotId: lot.lotId,
              unitPrice: lot.sellingPrice,
              lotQty: lot.qty,
              mrp: lot.mrp,
              requiresLot: true,
              lotChoice: 'user',
            });
          }}
        />
      )}

      {weighPick && (
        <WeighSheetModal
          item={weighPick.item}
          unitPrice={weighPick.unitPrice}
          stockCap={
            weighPick.spillLots && weighPick.mode !== 'edit'
              ? weighPick.stockCap
              : weighPick.mode === 'edit'
              ? weighPick.stockCap + (isEditMode ? (Number(amendStockCredit[weighPick.lineKey]) || 0) : 0)
              : Math.max(
                0,
                weighPick.stockCap
                  + (isEditMode ? (Number(amendStockCredit[cartLineKey(weighPick.item.id, weighPick.lotId)]) || 0) : 0)
                  - (isEditMode ? 0 : reservedQty(offlineSnap.charges, weighPick.item.id, weighPick.lotId))
                  - (cartQtyByLineKey[cartLineKey(weighPick.item.id, weighPick.lotId)] || 0),
              )
          }
          allocationLots={weighPick.spillLots ? weighPick.allocationLots : null}
          allocationTaken={weighPick.allocationTaken}
          initialWeight={weighPick.initialWeight}
          mode={weighPick.mode}
          qtyStep={weighPick.qtyStep}
          minOrderQty={weighPick.minOrderQty}
          maxOrderQty={weighPick.maxOrderQty}
          onClose={() => { setWeighPick(null); focusScanField(); }}
          onConfirm={(weightKg) => {
            const ctx = weighPick;
            setWeighPick(null);
            if (ctx.spillLots && ctx.mode !== 'edit') {
              const product = products.find((item) => item.id === ctx.item.id) || ctx.item;
              placeOfflinePieces(product, cart, weightKg, { keepValue: ctx.keepValue });
              return;
            }
            if (ctx.spillLots && ctx.mode === 'edit') {
              const product = products.find((item) => item.id === ctx.item.id) || ctx.item;
              const line = cart.find((entry) => entry.lineKey === ctx.lineKey);
              const stay = quantizeKg(Math.min(weightKg, Number(ctx.editLotCap) || 0)) ?? 0;
              const spill = quantizeKg(Math.max(0, weightKg - stay)) ?? 0;
              let next = cart;
              if (line && stay > 0 && stay !== line.qty) {
                pushQtyChange({ line: { ...line, qty: stay }, fromQty: line.qty, toQty: stay });
                next = cart.map((entry) => (entry.lineKey === line.lineKey ? { ...entry, qty: stay } : entry));
              }
              if (spill > 0) {
                placeOfflinePieces(product, next, spill, { keepValue: true });
                return;
              }
              if (next !== cart) {
                setCart(next);
                holdPaymentForMinOrder(next);
                setScanError('');
              }
              return;
            }
            addToCart(ctx.item, {
              keepValue: ctx.keepValue,
              lotId: ctx.lotId,
              unitPrice: ctx.unitPrice,
              lotQty: ctx.stockCap,
              mrp: ctx.mrp,
              requiresLot: ctx.requiresLot,
              lotChoice: ctx.lotChoice,
              qty: weightKg,
              soldBy: SOLD_BY.WEIGHT,
              replaceQty: ctx.mode === 'edit',
              fromCache: ctx.fromCache,
            });
          }}
        />
      )}

      {historyOpen && (
        <PosHistoryModal
          token={user.idToken}
          canEdit={canEditPosBills}
          visible={!isEditMode && !reprintOpen && !receipt}
          onClose={() => setHistoryOpen(false)}
          onOpenSale={(saleReceipt) => {
            setLastReceipt({
              ...saleReceipt,
              counter: saleReceipt.counter || POS_COUNTER,
              gstin: saleReceipt.gstin || POS_GSTIN,
              cashier_name: saleReceipt.cashier_name || user.name,
            });
            setReprintOpen(true);
          }}
          onEditSale={(salePayload) => {
            beginEditSale(salePayload);
          }}
          onVoided={(voidedReceipt) => {
            reloadProducts();
            refreshSessionStats();
            const confirmed = {
              ...voidedReceipt,
              counter: voidedReceipt.counter || POS_COUNTER,
              gstin: voidedReceipt.gstin || POS_GSTIN,
              cashier_name: voidedReceipt.cashier_name || user.name,
              voided: true,
            };
            setChangeDue(0);
            setLastReceipt(confirmed);
            setReceipt(confirmed);
            notify(`Bill ${confirmed.display_id || ''} voided — refund ${formatCurrency(confirmed.total || 0)}.`);
          }}
        />
      )}

      {amendPreview && (
        <PosAmendSettlementModal
          preview={amendPreview}
          preferredMethod={selectedMethods[0] || editSale?.originalPaymentMethod || ''}
          confirming={amending}
          repreviewing={repreviewing}
          confirmError={amendError}
          onClose={() => {
            if (amending || repreviewing) return;
            setAmendPreview(null);
            setAmendError('');
          }}
          onConfirm={handleConfirmAmend}
        />
      )}

      {receipt && (
        <Receipt
          mode="sale"
          receipt={receipt}
          changeDue={changeDue}
          token={user.idToken}
          products={products}
          onClose={closeReceipt}
        />
      )}

      {reprintOpen && lastReceipt && (
        <Receipt
          mode="reprint"
          receipt={lastReceipt}
          token={user.idToken}
          products={products}
          closeLabel={historyOpen ? 'Back' : 'Close'}
          onClose={() => setReprintOpen(false)}
        />
      )}
    </div>
  );
}

function ProductTile({ product, inCartQty, chargePrice, onAdd }) {
  const byWeight = isSoldByWeight(product);
  const canGapFill = product.catalogComplete === false;
  const priceLabel = chargePrice && typeof chargePrice === 'object'
    ? `${formatCurrency(chargePrice.min)} – ${formatCurrency(chargePrice.max)}${byWeight ? ` / ${product.unit || 'kg'}` : ''}`
    : formatPrice({ ...product, price: chargePrice }, formatCurrency);
  return (
    <button className={styles.tile} onClick={onAdd} type="button">
      <span className={styles.tileEmoji} style={{ background: `${product.swatch}22`, color: product.swatch }}>
        {product.emoji}
      </span>
      <span className={styles.tileName}>{product.name}</span>
      <span className={styles.tilePrice}>
        {priceLabel}
      </span>
      {byWeight && <span className={styles.tileWeightBadge}>Weigh</span>}
      {inCartQty > 0 ? (
        <span className={styles.tileInCart}>
          {byWeight ? `${formatQty(inCartQty, product)} in cart` : `${inCartQty} in cart`}
        </span>
      ) : canGapFill ? (
        <span className={styles.tileStock}>Available at POS</span>
      ) : (
        <span className={styles.tileStock}>{formatStock(product.stock, product)} left</span>
      )}
    </button>
  );
}
